import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactSlider from "react-slider";
import Header from "./Header";
import PropertyList from "./PropertyListCaroline";
import ParcelFilters from "./ParcelFilters";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";

mapboxgl.accessToken =
    "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("NCComponent Error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div style={{ padding: "20px", color: "red" }}>Error: {this.state.error?.message}</div>;
        }
        return this.props.children;
    }
}

const NCComponent = () => {
    const navigate = useNavigate();
    // const authToken = localStorage.getItem("authToken");
    const [authToken, setAuthToken] = useState(() => localStorage.getItem("authToken"));
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const legendControlRef = useRef(null);
    const boundsInitialized = useRef(false);  // Track if bounds have been set for current county
    const vacantFilterTouched = useRef(false);  // Track if vacant filter has ever been changed
    const zoneFilterTouched = useRef(false);  // Track if zone filter has ever been changed
    const listedSaleFilterTouched = useRef(false);  // Track if listed sale filter has ever been changed

    const [mapLoaded, setMapLoaded] = useState(false);
    const [selectedCounty, setSelectedCounty] = useState("all");
    const [countiesList, setCountiesList] = useState([]);
    const [countyCenters, setCountyCenters] = useState({});
    const [filteredParcels, setFilteredParcels] = useState([]);
    const [currentZoom, setCurrentZoom] = useState(6.5);
    const [isLoadingParcel, setIsLoadingParcel] = useState(false);
    const [selectedParcelId, setSelectedParcelId] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(
        localStorage.getItem("isSubscribed") === "true"
    );
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // API data state
    const [apiParcels, setApiParcels] = useState([]);
    const [filteredApiParcels, setFilteredApiParcels] = useState([]);
    const [isLoadingApi, setIsLoadingApi] = useState(false);
    const [totalParcelCount, setTotalParcelCount] = useState(0); // Total count from API

    // Filter states
    const [showPriceFilter, setShowPriceFilter] = useState(false);
    const [showSizeFilter, setShowSizeFilter] = useState(false);
    const [showFloodFilter, setShowFloodFilter] = useState(false);
    const [showZoneFilter, setShowZoneFilter] = useState(false);
    const [showMunicipalityFilter, setShowMunicipalityFilter] = useState(false);
    const [showZestimateFilter, setShowZestimateFilter] = useState(false);
    const [showSpreadFilter, setShowSpreadFilter] = useState(false);
    const [showListedSaleFilter, setShowListedSaleFilter] = useState(false);
    const [showAdjacentParcelFilter, setShowAdjacentParcelFilter] = useState(false);

    // Filter values
    const [priceRange, setPriceRange] = useState([0, 1000000]);
    const [sizeRange, setSizeRange] = useState([0, 100]);
    const [floodRiskFilter, setFloodRiskFilter] = useState("All");
    const [zoneCodeFilter, setZoneCodeFilter] = useState([]);
    const [zestimateRange, setZestimateRange] = useState([0, 1000000]);
    const [spreadRange, setSpreadRange] = useState([-100, 100]);
    const [listedSaleFilter, setListedSaleFilter] = useState("All");
    const [adjacentParcelFilter, setAdjacentParcelFilter] = useState("Suitable Parcels");
    // Sorting state
    const [sortBy, setSortBy] = useState('spreadpercentage');
    const [sortOrder, setSortOrder] = useState('desc');
    const [vacantFilter, setVacantFilter] = useState("All");

    // Slider bounds
    const [sliderMinPrice, setSliderMinPrice] = useState(0);
    const [sliderMaxPrice, setSliderMaxPrice] = useState(1000000);
    const [sliderMinSize, setSliderMinSize] = useState(0);
    const [sliderMaxSize, setSliderMaxSize] = useState(100);
    const [sliderMinZestimate, setSliderMinZestimate] = useState(0);
    const [sliderMaxZestimate, setSliderMaxZestimate] = useState(1000000);
    const [sliderMinSpread, setSliderMinSpread] = useState(-100);
    const [sliderMaxSpread, setSliderMaxSpread] = useState(100);

    // Filter options
    const [floodOptions, setFloodOptions] = useState(["All"]);
    const [listedSaleOptions, setListedSaleOptions] = useState([]);

    // Municipality 
    const [municipalityFilter, setMunicipalityFilter] = useState("All");
    const [municipalityOptions, setMunicipalityOptions] = useState(["All"]);
    const municipalityFilterTouched = useRef(false);

    // Track when slider drag is complete
    const [priceRangeDragComplete, setPriceRangeDragComplete] = useState(0);
    const [zestimateRangeDragComplete, setZestimateRangeDragComplete] = useState(0);
    const [sizeRangeDragComplete, setSizeRangeDragComplete] = useState(0);
    const [spreadRangeDragComplete, setSpreadRangeDragComplete] = useState(0);

    const [zoomBoundaryTrigger, setZoomBoundaryTrigger] = useState(0);
    const prevZoomRef = useRef(currentZoom);
    const minMaxInitialized = useRef(false);
    const [zoneCodes, setZoneCodes] = useState([]);
    const clickPopupRef = useRef(null);
    const hoverPopupRef = useRef(null);

    const wasAboveZoomThresholdRef = useRef(false);
    const selectedCountyRef = useRef("all");  // Track current selected county for event handlers

    const [listWidth, setListWidth] = useState(60); // percentage
    const [isParcelSearchActive, setIsParcelSearchActive] = useState(false);
    // Pagination state for Load More functionality (record-based offset)
    const [currentOffset, setCurrentOffset] = useState(0);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Map style state (light or satellite)
    const [mapStyle, setMapStyle] = useState('light'); // 'light' or 'satellite'
    const [resetParcelSearchSignal, setResetParcelSearchSignal] = useState(0);
    const [adjacentMode, setAdjacentMode] = useState(false);

    const latestFetchIdRef = useRef(0);
    const [mapInitError, setMapInitError] = useState(null);
    const filtersClearedRef = useRef(false); // Track when filters are being cleared
    const adjacentModeRef = useRef(adjacentMode);
    const floodRiskFilterTouched = useRef(false);






    const NC_CENTER = [-78.6443, 35.7877];

    const fetchParcelMinMax = async (county) => {
        try {
            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return null; }
            const url = adjacentMode
                ? `https://beta.flowlogic.com/api/assemblage/assemblage-minmax?county=${county}`
                : `https://beta.flowlogic.com/api/parcels/parcel-minmax/?county=${county}`;
            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (res.status === 401) { handleLogout(); return null; }
            if (!res.ok) throw new Error('Failed to fetch min/max');

            return await res.json();
        } catch (err) {
            console.error(' MinMax API error:', err);
            return null;
        }
    };
    const reloadTilesSafely = (mode) => {
        const map = mapRef.current;
        if (!map) return;

        if (!map.isStyleLoaded()) {
            map.once("styledata", () => reloadTilesSafely(mode));
            return;
        }

        reloadParcelTiles(mode);
    };
    useEffect(() => {
        if (!mapRef.current) return;

        console.log(" Adjacent mode:", adjacentMode);

        reloadTilesSafely(adjacentMode);

        // Restore parcel highlighting after tiles are reloaded
        setTimeout(() => {
            if (selectedCounty && selectedCounty !== "all") {
                updateLeftPanelParcels(selectedCounty);
                console.log(' Restored parcel highlighting after adjacent mode change');
            }
        }, 500);
    }, [adjacentMode]);

    useEffect(() => {
        adjacentModeRef.current = adjacentMode;
    }, [adjacentMode]);

    // Handle adjacent parcel filter changes
useEffect(() => {
    if (adjacentParcelFilter !== "All" && adjacentMode && selectedCounty !== "all") {
        // Reload API with adjacent parcel filter
        // fetchApiParcels();

          const isBest = adjacentParcelFilter === "Suitable Parcels" ? 1 : 0;
        // fetchApiParcels(selectedCounty, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, 0, null);
               //  NOW refetch the parcels using the new mode
            fetchApiParcels(
                selectedCounty,
                null, null,   // landValue
                null, null,   // zestimate
                null, null,   // parcelsize
                null, null,   // spread
                null,         // zone
                null,         // isVacant
                null,         // listedSale
                null,         // floodRisk
                sortBy,
                sortOrder,
                null,         // municipality
                0 ,isBest            // offset reset
            );
        // Reload tiles based on filter type
        // reloadTilesSafely(adjacentMode);  // ✅ Only pass mode
         reloadParcelTiles(adjacentMode);

                
        // Preserve and reapply county filter after tile reload
        setTimeout(() => {
            if (mapRef.current && selectedCounty) {
                mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                mapRef.current.setFilter("parcel-points", filterExpr);
                if (mapRef.current.getLayer("parcel-outline")) {
                    mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                    mapRef.current.setFilter("parcel-outline", filterExpr);
                }

                    
  
          if (mapRef.current.getLayer("parcel-selected-outline")) {
        mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
    }
                
            }
        }, 1000);
        console.log(" Adjacent parcel filter changed:", adjacentParcelFilter);
    }
}, [adjacentParcelFilter, adjacentMode, selectedCounty,selectedCountyRef.current]);


    // Swap parcel tiles between base and adjacent without navigation
    const reloadParcelTiles = (mode) => {
        if (!mapRef.current) return;

        // const parcelsLayers = ["parcel-selected-outline", "parcel-selected-fill", "parcel-hover", "parcel-outline", "parcel-points"];
        const parcelsLayers = ["parcel-selected-outline", "parcel-hover", "parcel-outline", "parcel-points"];
        parcelsLayers.forEach((id) => {
            if (mapRef.current.getLayer(id)) {
                try { mapRef.current.removeLayer(id); } catch (e) { }
            }
        });

        if (mapRef.current.getSource("parcels")) {
            try { mapRef.current.removeSource("parcels"); } catch (e) { }
        }

        const tilesUrl = mode
            // ? "http://localhost:8008/data/adjparcel1s/{z}/{x}/{y}.pbf"
            // ? "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf"
            ? adjacentParcelFilter === "Suitable Parcels" 
                // ? "http://localhost:8007/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
                // : "http://localhost:8007/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

            ? "https://demo.flowlogic.com/tilesgl/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
            : "https://demo.flowlogic.com/tilesgl/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

                // : "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf"
            : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf";

            console.log(" tilesUrl", tilesUrl);

        mapRef.current.addSource("parcels", {
            type: "vector",
            tiles: [tilesUrl],
        });

        // Re-add layers with same styling
        mapRef.current.addLayer({
            id: "parcel-points",
            type: "fill",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                "fill-color": [
                    "match",
                    ["get", "parcelcustomfemaflood"],
                    "X", "#4CAF50",
                    "AE", "#FF9800",
                    "VE", "#E91E63",
                    "A", "#2196F3",
                    "#9fa2e7",
                ],
                "fill-opacity": 0.45,
            },
        });

        mapRef.current.addLayer({
            id: "parcel-outline",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                "line-color": [
                    "step",
                    ["zoom"],
                    "#333",
                    13, "#ebeaea",
                ],
                "line-width": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0, 0.5,
                    13, 1.0,
                    18, 2.0,
                ],
                "line-opacity": [
                    "step",
                    ["zoom"],
                    0,
                    13, 0.9,
                ],
            },
        });

        mapRef.current.addLayer({
            id: "parcel-hover",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                "line-color": "#ff7300",
                "line-width": 3,
                "line-opacity": 0,
            },
            filter: ["==", ["get", "parcel"], ""],
        });

        // Selected parcels fill layer (for row click highlight)
        // adjacentMode && mapRef.current.addLayer({
        //     id: "parcel-selected-fill",
        //     type: "fill",
        //     source: "parcels",
        //     "source-layer": "fema",
        //     layout: { visibility: "none" },
        //     paint: {
        //         "fill-color": "#1c8f7d",
        //         "fill-opacity": 0.4,
        //     },
        //     filter: ["in", ["get", "parcel"], ["literal", []]],
        // });

        // Selected parcels outline layer
        adjacentMode && mapRef.current.addLayer({
            id: "parcel-selected-outline",
            type: "line",
            source: "parcels",
            "source-layer": "fema",
            layout: { visibility: "none" },
            paint: {
                // "line-color": "#0f5d51",
                "line-color": "#eaff4a",
                "line-width":3,
                "line-opacity": 0.95,
            },
            filter: ["in", ["get", "parcel"], ["literal", []]],
        });

        // Ensure layers reflect current app state immediately
        const currentZoomNow = mapRef.current.getZoom();
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        mapRef.current.once('idle', () => {
            try {
                // Show parcel layers only when zoomed in (and optionally hide when filters active)
                const showTiles = currentZoomNow >= 13.1 && !hasActiveFilters;
                mapRef.current.setLayoutProperty('parcel-points', 'visibility', showTiles ? 'visible' : 'none');
                mapRef.current.setLayoutProperty('parcel-outline', 'visibility', showTiles ? 'visible' : 'none');

                // Reapply county filter after tile reload (prevents momentary hidden state)
                const county = selectedCountyRef.current;
                if (county && county !== 'all') {
                    const filterExpr = ["==", ["downcase", ["get", "county"]], county.toLowerCase()];
                    mapRef.current.setFilter('parcel-points', filterExpr);
                    if (mapRef.current.getLayer('parcel-outline')) {
                        mapRef.current.setFilter('parcel-outline', filterExpr);
                    }
                    // Ensure layer is visible when a county is selected
                    mapRef.current.setLayoutProperty('parcel-points', 'visibility', 'visible');
                    mapRef.current.setLayoutProperty('parcel-outline', 'visibility', 'visible');
                }

                // Keep counties above parcels
                mapRef.current.moveLayer('counties-fill', 'parcel-points');
                mapRef.current.moveLayer('counties-outline', 'parcel-points');
            } catch (e) { }

            try { mapRef.current.triggerRepaint && mapRef.current.triggerRepaint(); } catch (e) { }
        });
    };
    useEffect(() => {
        minMaxInitialized.current = false;
        selectedCountyRef.current = selectedCounty;  // Keep ref in sync with state

        if (!selectedCounty || selectedCounty === "all") return;

        //  Reset municipality filter when county changes
        setMunicipalityFilter("All");

        // If you track touched state
        municipalityFilterTouched.current = false;
    }, [selectedCounty]);

    // Reinitialize min/max when adjacent mode toggles

    // useEffect(() => {
    //     minMaxInitialized.current = false;
    //     if (!selectedCounty || selectedCounty === 'all') return;
    //     // Trigger the init effect below by calling fetchParcelMinMax directly
    //     (async () => {
    //         const data = await fetchParcelMinMax(selectedCounty);
    //         if (!data) return;
    //         // ---- Price ----
    //         setSliderMinPrice(data.totallandvalue_min);
    //         setSliderMaxPrice(data.totallandvalue_max);
    //         setPriceRange([data.totallandvalue_min, data.totallandvalue_max]);

    //         // ---- Size ----
    //         setSliderMinSize(Number(data.parcelsize_min));
    //         setSliderMaxSize(Number(data.parcelsize_max));
    //         setSizeRange([Number(data.parcelsize_min), Number(data.parcelsize_max)]);

    //         // ---- Zestimate ----
    //         setSliderMinZestimate(data.zestimate_min);
    //         setSliderMaxZestimate(data.zestimate_max);
    //         setZestimateRange([data.zestimate_min, data.zestimate_max]);

    //         // ---- Zone codes ----
    //         const zoneCodes = (data.zonedcodelocal_values || [])
    //             .filter(Boolean)
    //             .sort();
    //         setZoneCodes(zoneCodes);

    //         // ---- Spread Percentage ----
    //         const minSpread = (data.spreadpercentage_min);
    //         const maxSpread = (data.spreadpercentage_max);
    //         setSliderMinSpread(minSpread);
    //         setSliderMaxSpread(maxSpread);
    //         setSpreadRange([minSpread, maxSpread]);

    //          // ---- Flood risk ----
    //         const floodRisks = (data.parcelcustomfemaflood_values || [])
    //             .filter(Boolean)
    //             .sort();

    //         // Municipality 
    //         setMunicipalityOptions([
    //             "All",
    //             ...(data.municipality_values || []).filter(Boolean).sort(),
    //         ]);

    //         minMaxInitialized.current = true;
    //     })();
    // }, [adjacentMode]);

    useEffect(() => {
        console.log("@@@@@*****INSIDE UEEEFFECT ");
        if (!selectedCounty || selectedCounty === "all") return;

        console.log("@@@@@*****AFTER UEEEFFECT ");
        console.log("@@@@@*****selcted county ",selectedCounty);


        console.log(" Adjacent mode changed:", adjacentMode);

        // Reset min/max cache
        minMaxInitialized.current = false;

        //  CRITICAL FIX: Reset pagination offset when mode changes
        setCurrentOffset(0);
        setHasMoreData(true);
        setIsLoadingMore(false);

        //  FIX: Clear API parcels IMMEDIATELY to prevent old data from showing
        setApiParcels([]);
        setFilteredApiParcels([]);

        (async () => {
            // 1 Reload min/max for the new mode
            const data = await fetchParcelMinMax(selectedCounty);
            if (!data) return;

            // ---- Price ----
            setSliderMinPrice(data.totallandvalue_min);
            setSliderMaxPrice(data.totallandvalue_max);
            setPriceRange([data.totallandvalue_min, data.totallandvalue_max]);

            // ---- Size ----
            setSliderMinSize(Number(data.parcelsize_min));
            setSliderMaxSize(Number(data.parcelsize_max));
            setSizeRange([Number(data.parcelsize_min), Number(data.parcelsize_max)]);

            // ---- Zestimate ----
            setSliderMinZestimate(data.zestimate_min);
            setSliderMaxZestimate(data.zestimate_max);
            setZestimateRange([data.zestimate_min, data.zestimate_max]);

            // ---- Spread ----
            setSliderMinSpread(data.spreadpercentage_min);
            setSliderMaxSpread(data.spreadpercentage_max);
            setSpreadRange([data.spreadpercentage_min, data.spreadpercentage_max]);

            // ---- Zone ----
            setZoneCodes((data.zonedcodelocal_values || []).filter(Boolean).sort());

            // ---- Flood ----
            setFloodOptions(["All", ...(data.parcelcustomfemaflood_values || []).filter(Boolean).sort()]);

            // ---- Municipality ----
            setMunicipalityOptions(["All", ...(data.municipality_values || []).filter(Boolean).sort()]);

            minMaxInitialized.current = true;

            // RESET all filters so stale ones don’t block adjacency
            setZoneCodeFilter([]);
            setVacantFilter("All");
            setListedSaleFilter("All");
            setFloodRiskFilter("All");
            setMunicipalityFilter("All");
            //  Clear API parcels IMMEDIATELY to prevent old data from showing
            setApiParcels([]);
            setFilteredApiParcels([]);

            //  NOW refetch the parcels using the new mode
            fetchApiParcels(
                selectedCounty,
                null, null,   // landValue
                null, null,   // zestimate
                null, null,   // parcelsize
                null, null,   // spread
                null,         // zone
                null,         // isVacant
                null,         // listedSale
                null,         // floodRisk
                sortBy,
                sortOrder,
                null,         // municipality
                0             // offset reset
            );
     // Reapply county filter after tile reload
        setTimeout(() => {
            if (mapRef.current && selectedCounty) {
                mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                mapRef.current.setFilter("parcel-points", filterExpr);
                if (mapRef.current.getLayer("parcel-outline")) {
                    mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                    mapRef.current.setFilter("parcel-outline", filterExpr);
                }
            }
        }, 1000);
            
    //         if (adjacentMode && selectedCounty !== "all") {
    //              fetchParcelMinMax(selectedCounty, adjacentMode);
    //     // Reload tiles when county changes in adjacent mode
    //     reloadParcelTiles(adjacentMode);
        
    //     // Reapply county filter after tile reload
    //     setTimeout(() => {
    //         if (mapRef.current && selectedCounty) {
    //             mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
    //             const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
    //             mapRef.current.setFilter("parcel-points", filterExpr);
    //             if (mapRef.current.getLayer("parcel-outline")) {
    //                 mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
    //                 mapRef.current.setFilter("parcel-outline", filterExpr);
    //             }
    // //                 if (mapRef.current.getLayer("parcel-selected-outline")) {
    // //     mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
    // // }
                
    
    //         }
    //     }, 1000);
    // }

        })();
    }, [adjacentMode, selectedCounty]);



    //clear parcel search
    const clearParcelSearch = () => {
        setIsParcelSearchActive(false);
        updateLeftPanelParcels(selectedCounty)
        // clearAllFilters();
        clearFiltersForSearch();
        setSelectedParcelId(null);
        clearParcelHighlight();

        fetchApiParcels(
            selectedCounty,
            priceRange[0],
            priceRange[1],
            zestimateRange[0],
            zestimateRange[1],
            sizeRange[0],
            sizeRange[1],
            spreadRange[0],
            spreadRange[1],
            zoneCodeFilter.join(","),
            vacantFilter !== "All" ? vacantFilter : null,
            listedSaleFilter !== "All" ? listedSaleFilter : null,
            floodRiskFilter !== "All" ? floodRiskFilter : null,
            sortBy,
            sortOrder,
            municipalityFilter
        );
    };

    const PARCEL_LENGTH_BY_COUNTY = {
        default: 7,
        wake: 10,
    };
    const getMinParcelLength = (county) => {
        if (!county) return PARCEL_LENGTH_BY_COUNTY.default;
        return PARCEL_LENGTH_BY_COUNTY[county.toLowerCase()] || PARCEL_LENGTH_BY_COUNTY.default;
    };
    const minLength = getMinParcelLength(selectedCounty);


    //fetch search parcel
    const searchParcelApi = async (parcelNumber) => {

        //  Guard: invalid input
        if (!parcelNumber || parcelNumber.length < minLength || !selectedCounty || selectedCounty === "all") {
            return []; //  explicit return
        }
        clearFiltersForSearch();


        try {
            setIsLoadingApi(true);
            // clearAllFilters();


            const params = new URLSearchParams({
                parcel: parcelNumber,
                county: selectedCounty,
            });

            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }

            const baseUrl = adjacentMode
                ? "https://beta.flowlogic.com/api/assemblage/search/"
                : "https://beta.flowlogic.com/api/parcels/search/";


            

            const resp = await fetch(
                // `https://beta.flowlogic.com/api/parcels/search/?${params.toString()}`,
                `${baseUrl}?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (resp.status === 401) { handleLogout(); return; }

            if (!resp.ok) throw new Error("Search failed");

            const data = await resp.json();

            const items =
                data?.features?.map(f => f.properties) ||
                data?.results ||
                data ||
                [];
                

            setApiParcels(items);
            setFilteredParcels(items);
            setIsParcelSearchActive(true);
            updateMapWithSearchParcels(items);
            // setSelectedParcelId(parcelNumber);
            if (items.length > 0) {
    const first = items[0];

    const parcelId =
        first.parcel ||
        first.parcelid ||
        first.geo_id ||
        first.id;

    const touchingId =
        first?.touching_parcel ||
        first?.adjacent_parcel ||
        null;

    setSelectedParcelId(
        adjacentMode ? `${parcelId}-${touchingId || ''}` : parcelId
    );
}

        } catch (e) {
            console.error("Parcel search error", e);
            setApiParcels([]);
            setFilteredParcels([]);
        } finally {
            setIsLoadingApi(false);
        }
    };
    


    // Fetch parcels from API
    const fetchApiParcels = async (countyFilter, landValueMin = null, landValueMax = null, zestimateMin = null, zestimateMax = null, parcelsizeMin = null, parcelsizeMax = null, spreadMin = null, spreadMax = null, zoneCode = null, isVacant = null, listedSale = null, floodRisk = null, sortBy = null, sortOrder = 'desc', municipality = null, offsetNum = 0,is_best = null) => {
        //  if (isParcelSearchActive) return;
        setResetParcelSearchSignal(prev => prev + 1);

        const fetchId = ++latestFetchIdRef.current;

        if (countyFilter === "all") {
            setApiParcels([]);
            return;
        }
        console.log("countyFilter", countyFilter, "municipality", municipality, "landValueMin", landValueMin, "landValueMax", landValueMax, "zestimateMin", zestimateMin, "zestimateMax", zestimateMax, "parcelsizeMin", parcelsizeMin, "parcelsizeMax", parcelsizeMax, "spreadMin", spreadMin, "spreadMax", spreadMax, "zoneCode", zoneCode, "isVacant", isVacant, "listedSale", listedSale,"is_best", is_best);

        // Only set isLoadingApi for initial load, not for "Load More"
        // if (offsetNum === 0) {
        //     setIsLoadingApi(true);
        // }

        // Only set isLoadingApi for initial load, not for "Load More"
        if (offsetNum === 0) {
            // clear previous results immediately for fresh queries
            setApiParcels([]);
            setFilteredApiParcels([]);
            setIsLoadingApi(true);
            setHasMoreData(true);
            setCurrentOffset(0);
        }

        try {

            clickPopupRef.current?.remove();
            clickPopupRef.current = null;

            const params = new URLSearchParams();
            params.append("limit", "100");
            params.append("offset", String(offsetNum)); // Record offset: 0, 100, 200, ...

            console.log(`🔍 API Call - Offset: ${offsetNum}, Limit: 100, Expected records: ${offsetNum * 5}-${(offsetNum * 5) + 499}`);


            // if (countyFilter && countyFilter !== "all" && !adjacentMode) {
            if (countyFilter && countyFilter !== "all") {
                params.append("county", countyFilter);
            }

            // Add land value filter parameters if provided
            if (landValueMin !== null) {
                params.append("totallandvalue_min", landValueMin);
            }
            if (landValueMax !== null) {
                params.append("totallandvalue_max", landValueMax);
            }

            // Add zestimate filter parameters if provided
            if (zestimateMin !== null) {
                params.append("zestimate_min", zestimateMin);
            }
            if (zestimateMax !== null) {
                params.append("zestimate_max", zestimateMax);
            }

            // Add parcelsize filter parameters if provided
            if (parcelsizeMin !== null) {
                params.append("parcelsize_min", parcelsizeMin);
            }
            if (parcelsizeMax !== null) {
                params.append("parcelsize_max", parcelsizeMax);
            }

            // Add spread filter parameters if provided
            if (spreadMin !== null) {
                params.append("spreadpercentage_min", spreadMin);
            }
            if (spreadMax !== null) {
                params.append("spreadpercentage_max", spreadMax);
            }

            // Add zone code filter parameter if provided
            if (zoneCode !== null && zoneCode !== "" && typeof zoneCode === 'string' && zoneCode.trim() !== "") {
                params.append("zonedcodelocal", zoneCode);
            } else if (Array.isArray(zoneCode) && zoneCode.length > 0) {
                params.append("zonedcodelocal", zoneCode.join(","));
            }

            // Add vacant filter parameter if provided
            if (isVacant !== null) {
                params.append("is_vaccant", isVacant);
            }

            // Add listed sale filter parameter if provided
            if (listedSale !== null) {
                params.append("listedsale", listedSale);
            }

            // Add flood risk filter parameter if provided
            if (floodRisk !== null && floodRisk !== "All") {
                params.append("flood_risk", floodRisk);
            }

            // Add sorting parameters if provided
            if (sortBy !== null) {
                params.append("sort_by", sortBy);
            }
            if (sortOrder !== null) {
                params.append("sort_order", sortOrder);
            }

            // Add municipality filter parameter if provided
            if (municipality !== null && municipality !== "All" && municipality.trim() !== "") {
                params.append("municipality", municipality);
            }

                        // Add this to the API parameters
if (adjacentMode && adjacentParcelFilter === "Suitable Parcels") {
    params.append('is_best', '1');
}
 else if (adjacentMode && adjacentParcelFilter !== "All") {
    params.append('is_best', '0');
}

if (adjacentMode) {
    if (adjacentParcelFilter === "Suitable Parcels") {
        params.append('is_best', '1');  // Suitable parcels
    } else {
        params.append('is_best', '0');  // Unsuitable parcels
    }
}
            // if (is_best !== null) {
    // params.append('is_best', is_best.toString());// Add this to the API parameters
// if (adjacentMode && adjacentParcelFilter !== "Suitable Parcels") {
//     params.append('is_best', '0');
// } else if (adjacentMode && adjacentParcelFilter !== "All") {
//     params.append('is_best', '0');
// }
// }

            const url = adjacentMode ? `https://beta.flowlogic.com/api/assemblage/list-assemblage-parcel-data/?${params.toString()}` : `https://beta.flowlogic.com/api/parcels/list-parcel-data/?${params.toString()}`;

            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }
            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });

            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }

            const data = await resp.json();
            console.log("API Response:", data);

            // Handle different response formats
            let items = [];
            if (data?.features && Array.isArray(data.features)) {
                // FeatureCollection format
                items = data.features.map(f => f.properties || f);
            } else if (Array.isArray(data?.results)) {
                // results array format
                items = data.results;
            } else if (Array.isArray(data)) {
                // plain array format
                items = data;
            }

            // // Append if loading more (offsetNum > 0), otherwise replace
            // setApiParcels(prev => offsetNum > 0 ? [...prev, ...items] : items);

            // // Check if there's more data (if we got 100 items, there might be more)
            // setHasMoreData(items.length >= 100);

            // Ignore stale responses
            if (fetchId !== latestFetchIdRef.current) {
                console.log(`Stale API response (id ${fetchId}) — ignoring`);
                return;
            }

            // Append if loading more (offsetNum > 0), otherwise replace
            setApiParcels(prev => offsetNum > 0 ? [...prev, ...items] : items);

            // Check if there's more data (if we got 100 items, there might be more)
            setHasMoreData(items.length >= 100);

            // Debug: Log what records were actually received
            if (items.length > 0) {
                const firstParcel = items[0]?.parcel || items[0]?.parcelid || 'unknown';
                const lastParcel = items[items.length - 1]?.parcel || items[items.length - 1]?.parcelid || 'unknown';
                console.log(`📦 Received ${items.length} parcels. First: ${firstParcel}, Last: ${lastParcel}`);
            } else {
                console.log(`📦 Received 0 parcels from API (offsetNum: ${offsetNum})`);
            }

            console.log(`API Response: Fetched ${items.length} parcels from API for ${countyFilter} with applied filters`);
        } catch (e) {
            console.error("API fetch error:", e);
            // setApiParcels([]);
            // setHasMoreData(false);

            // Only clear if this is the latest fetch
            if (fetchId === latestFetchIdRef.current) {
                setApiParcels([]);
                setFilteredApiParcels([]);
                setHasMoreData(false);
            }
        } finally {
            // // Only reset isLoadingApi for initial load
            // if (offsetNum === 0) {
            //     setIsLoadingApi(false);
            // }
            // setIsLoadingMore(false);

            // Only update loading flags for the latest fetch
            if (fetchId === latestFetchIdRef.current) {
                if (offsetNum === 0) setIsLoadingApi(false);
                setIsLoadingMore(false);
            } else {
                // stale response — don't modify loading flags
            }
        }


    };

    // Load more parcels for pagination
    const loadMoreParcels = () => {

        //  Block pagination while search is active
        if (isParcelSearchActive) {
            console.log("Load more blocked — search is active");
            return;
        }
        if (isLoadingMore || !hasMoreData || isLoadingApi) {
            return;
        }

        const nextOffset = currentOffset + 100;
        setIsLoadingMore(true);
        setCurrentOffset(nextOffset);

        // Check if filters are active
        const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
            (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

        const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
            (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

        const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
            (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

        const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
            (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

        console.log("🔍 Filter Detection:", { isPriceFilterActive, isZestimateFilterActive, isSizeFilterActive, isSpreadFilterActive, priceRange, sizeRange, vacantFilter });

        // Call API with active filters
        fetchApiParcels(
            selectedCounty,
            isPriceFilterActive ? priceRange[0] : null,
            isPriceFilterActive ? priceRange[1] : null,
            isZestimateFilterActive ? zestimateRange[0] : null,
            isZestimateFilterActive ? zestimateRange[1] : null,
            isSizeFilterActive ? sizeRange[0] : null,
            isSizeFilterActive ? sizeRange[1] : null,
            isSpreadFilterActive ? spreadRange[0] : null,
            isSpreadFilterActive ? spreadRange[1] : null,
            zoneCodeFilter && zoneCodeFilter.length > 0 ? zoneCodeFilter : null,
            vacantFilter !== "All" ? vacantFilter : null,
            listedSaleFilter !== "All" ? listedSaleFilter : null,
            floodRiskFilter !== "All" ? floodRiskFilter : null,
            sortBy,
            sortOrder,
            municipalityFilter !== "All" ? municipalityFilter : null,
            nextOffset
        );
    };

    // Fetch total count from parcel-minmax API
    const fetchTotalCount = async (countyFilter) => {
        if (!countyFilter || countyFilter === "all") {
            setTotalParcelCount(0);
            return;
        }

        try {
            const url = adjacentMode
                ? `https://beta.flowlogic.com/api/assemblage/assemblage-minmax?county=${countyFilter}`
                : `https://beta.flowlogic.com/api/parcels/parcel-minmax/?county=${countyFilter}`;
            const token = localStorage.getItem("authToken");
            if (!token) { handleLogout(); return; }


            const resp = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                },
            });

            if (resp.status === 401) { handleLogout(); return; }
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }

            const data = await resp.json();
            console.log("Total Count API Response:", data);

            // Extract total count from response
            const count = data?.count || data?.total || 0;
            setTotalParcelCount(count);
        } catch (e) {
            console.error("Total count fetch error:", e);
            setTotalParcelCount(0);
        }
    };

    // Format currency helper
    // const formatCurrency = (num) => num ? "$" + num.toLocaleString("en-US") : "N/A";
    const formatCurrency = (num) => (num !== null && num !== undefined) ? "$" + num.toLocaleString("en-US") : "N/A";

    // Compute zone codes from API parcels
    // const zoneCodes = useMemo(() => {
    //     const codes = new Set();
    //     apiParcels.forEach((p) => {
    //         const z = p.zonedcodelocal ?? p.ZonedCodeLocal ?? p.ZonedCode ?? p.zoning ?? "Unknown";
    //         if (z) codes.add(String(z));
    //     });
    //     return [...Array.from(codes).sort()];
    // }, [apiParcels]);

    const clearFiltersForSearch = () => {
        // setPriceRange([sliderMinPrice ?? 0, sliderMaxPrice ?? 1000000]);
        // setSizeRange([sliderMinSize ?? 0, sliderMaxSize ?? 100]);
        // setZestimateRange([sliderMinZestimate ?? 0, sliderMaxZestimate ?? 1000000]);
        // setSpreadRange([sliderMinSpread ?? -100, sliderMaxSpread ?? 100]);


        if (sliderMinPrice !== undefined && sliderMaxPrice !== undefined) {
            setPriceRange([sliderMinPrice, sliderMaxPrice]);
        } else {
            setPriceRange([0, 10000000]); // Wide open fallback if no data
        }

        if (sliderMinSize !== undefined && sliderMaxSize !== undefined) {
            setSizeRange([sliderMinSize, sliderMaxSize]);
        } else {
            setSizeRange([0, 100000]); // Wide open fallback
        }

        if (sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined) {
            setZestimateRange([sliderMinZestimate, sliderMaxZestimate]);
        } else {
            setZestimateRange([0, 10000000]); // Wide open fallback
        }

        if (sliderMinSpread !== undefined && sliderMaxSpread !== undefined) {
            setSpreadRange([sliderMinSpread, sliderMaxSpread]);
        } else {
            setSpreadRange([-100, 100]); // Wide open fallback
        }
        setFloodRiskFilter("All");
        setZoneCodeFilter([]);
        setListedSaleFilter("All");
        setVacantFilter("All");
        setMunicipalityFilter("All");

        vacantFilterTouched.current = false;
        zoneFilterTouched.current = false;
        listedSaleFilterTouched.current = false;
        municipalityFilterTouched.current = false;

        // close dropdowns
        setShowPriceFilter(false);
        setShowSizeFilter(false);
        setShowFloodFilter(false);
        setShowZoneFilter(false);
        setShowMunicipalityFilter(false);
        setShowZestimateFilter(false);
        setShowSpreadFilter(false);
        setShowListedSaleFilter(false);
    };

    // Clear all filters function
    const clearAllFilters = () => {

        // Set flag to indicate filters are being cleared
        filtersClearedRef.current = true;

        setResetParcelSearchSignal(prev => prev + 1);
        // Clear API parcels so list can refresh with tile data if zoom >=13.1
        clearFiltersForSearch();
        setApiParcels([]);

        clickPopupRef.current?.remove();
        clickPopupRef.current = null;


        // Ensure parcel layers remain visible if county is selected
        // Use setTimeout to ensure this runs after state updates
        setTimeout(() => {
            if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
                updateLeftPanelParcels(selectedCounty);
                console.log(' Clear filters: Ensuring parcel layers visible');
            }
        }, 100);
    };


    // Clear all filters function
    // const clearAllFilters = () => {

    //     setResetParcelSearchSignal(prev => prev + 1);

    //        //  FIX: Reset pagination state
    //     setCurrentOffset(0);
    //     setHasMoreData(true);
    //         // Clear all filters function
    // const clearAllFilters = () => {

    //     setResetParcelSearchSignal(prev => prev + 1);

    //     // ✅ FIX: Reset pagination state
    //     setCurrentOffset(0);
    //     setHasMoreData(true);
    //     setIsLoadingMore(false);

    //     // Clear API parcels so list can refresh with tile data if zoom >=13.1
    //     clearFiltersForSearch();

    //     // ✅ CRITICAL: Clear both apiParcels AND filteredApiParcels
    //     setApiParcels([]);
    //     setFilteredApiParcels([]);

    //     clickPopupRef.current?.remove();
    //     clickPopupRef.current = null;

    //     // Ensure parcel layers remain visible if county is selected
    //     // Use setTimeout to ensure this runs after state updates
    //     setTimeout(() => {
    //         if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
    //             updateLeftPanelParcels(selectedCounty);
    //             console.log(' Clear filters: Ensuring parcel layers visible');
    //         }
    //     }, 100);
    // };

    //     // Clear API parcels so list can refresh with tile data if zoom >=13.1
    //     clearFiltersForSearch();
    //     setApiParcels([]);


    //     clickPopupRef.current?.remove();
    //     clickPopupRef.current = null;


    //     // Ensure parcel layers remain visible if county is selected
    //     // Use setTimeout to ensure this runs after state updates
    //     setTimeout(() => {
    //         if (selectedCounty && selectedCounty !== "all" && mapRef.current) {
    //             updateLeftPanelParcels(selectedCounty);
    //             console.log(' Clear filters: Ensuring parcel layers visible');
    //         }
    //     }, 100);
    // };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLogout = () => {

        localStorage.removeItem("access");
        localStorage.removeItem("refresh");

        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("email");
        localStorage.removeItem("isSubscribed");
        navigate("/");
    };

    // Keep authToken in sync (storage + interval)
    useEffect(() => {
        const syncToken = () => setAuthToken(localStorage.getItem("authToken"));
        window.addEventListener("storage", syncToken);
        const id = setInterval(syncToken, 30000); // re-check every 30s
        return () => {
            window.removeEventListener("storage", syncToken);
            clearInterval(id);
        };
    }, []);


    // Authentication check - redirect to login if not authenticated
    useEffect(() => {
        console.log("🔍 Auth check: token =", authToken ? "true" : "false");
        const checkTokenValidity = () => {
            if (!authToken) {
                // navigate("/");
                handleLogout();
                return;
            }

            try {
                const decoded = jwtDecode(authToken);
                const isExpired = decoded.exp * 1000 < Date.now();

                if (isExpired) {
                    console.warn("⏰ Token expired");
                    // Clear storage
                    // localStorage.removeItem("authToken");
                    // localStorage.removeItem("refreshToken");
                    // localStorage.removeItem("email");
                    // localStorage.removeItem("isSubscribed");
                    // navigate("/");
                    handleLogout();
                }
            } catch (e) {
                console.error("🚨 Token decode error:", e);
                // If token is malformed or can't be decoded

                // localStorage.removeItem("authToken");
                // navigate("/");
                handleLogout();
            }
        };

        checkTokenValidity();
        // }, [authToken, navigate]);
    }, [authToken]);

    // Fly to largest parcel when filters are applied
    useEffect(() => {
        // Only proceed if map is ready and we have API parcels
        if (!mapRef.current || !apiParcels || apiParcels.length === 0) return;

        // Check if any filters are active (including municipality)
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        // Only fly to parcel if filters are active
        if (!hasActiveFilters) return;

        // Find parcel with maximum parcelsize
        const maxParcel = apiParcels.reduce((max, p) => {
            const currentSize = parseFloat(p.parcelsize || 0);
            const maxSize = parseFloat(max.parcelsize || 0);
            console.log(`Current parcel size: ${currentSize}, Max parcel size: ${maxSize}`);
            return currentSize > maxSize ? p : max;
        }, apiParcels[0]);

        // Fly to the coordinates of the largest parcel
        if (maxParcel && maxParcel.latitude && maxParcel.longitude) {
            const lat = Number(maxParcel.latitude);
            const lon = Number(maxParcel.longitude);

            console.log(`Flying to largest parcel with size ${maxParcel.parcelsize} at [${lon}, ${lat}]`);
            console.log(`Parcel details:`, maxParcel);

            // Calculate offset for left panel (same as row click handler)
            const screenWidth = window.innerWidth;
            // const leftPanelWidth = screenWidth * 0.60;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            // Use flyTo with precise coordinates and offset
            mapRef.current.flyTo({
                center: [lon, lat],
                zoom: 11,
            });
        }
    }, [apiParcels]); // Only depend on apiParcels - triggers when API returns new results after drag complete


    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current) return;
        if (mapRef.current) return; // prevent double-init

        const styleUrl = mapStyle === 'satellite'
            ? "mapbox://styles/mapbox/satellite-streets-v12"
            : "mapbox://styles/mapbox/light-v11";

        // mapRef.current = new mapboxgl.Map({
        //     container: mapContainerRef.current,
        //     style: styleUrl,
        //     center: NC_CENTER,
        //     zoom: 6.5,
        //     maxZoom: 22,
        // });

        try {
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: styleUrl,
                center: NC_CENTER,
                zoom: 6.5,
                maxZoom: 22,
            });
        } catch (e) {
            console.error("Map init failed:", e);
            setMapInitError(e);
            return;
        }

        // Add map style toggle control
        class StyleToggleControl {
            onAdd(map) {
                this._map = map;
                this._container = document.createElement('div');
                this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

                this._button = document.createElement('button');
                this._button.type = 'button';
                this._button.style.width = '100px';
                this._button.style.padding = '8px';
                this._button.style.cursor = 'pointer';
                this._button.style.fontSize = '13px';
                this._button.style.fontWeight = '500';
                this._button.style.display = 'flex';
                this._button.style.alignItems = 'center';
                this._button.style.justifyContent = 'center';
                this._button.innerHTML = 'Satellite';

                this._button.onclick = () => {
                    const currentStyle = this._map.getStyle().sprite.includes('satellite') ? 'satellite' : 'light';
                    const newStyle = currentStyle === 'light' ? 'satellite' : 'light';
                    const styleUrl = newStyle === 'satellite'
                        ? "mapbox://styles/mapbox/satellite-streets-v12"
                        : "mapbox://styles/mapbox/light-v11";

                    // Update button text
                    this._button.innerHTML = newStyle === 'light' ? 'Satellite' : 'Light';

                    // Store current layers and sources before changing style
                    const currentLayers = [];
                    const currentSources = {};
                    const layerPaintProperties = {};

                    // Save custom layers (not built-in ones)
                    this._map.getStyle().layers.forEach(layer => {
                        // Save any layer that's custom or has been modified
                        if (layer.id.includes('parcel') ||
                            layer.id.includes('counties') ||
                            layer.id.includes('highlight') ||
                            layer.id === 'counties-fill' ||
                            layer.id === 'counties-outline' ||
                            layer.id === 'parcel-points' ||
                            layer.id.includes('nc-') ||
                            layer.source === 'composite' && layer.id.includes('admin')) {
                            currentLayers.push(layer);
                            // Store paint properties
                            if (layer.paint) {
                                layerPaintProperties[layer.id] = layer.paint;
                            }
                        }
                    });

                    // Save custom sources
                    Object.keys(this._map.getStyle().sources).forEach(sourceId => {
                        if (sourceId.includes('parcel') ||
                            sourceId.includes('counties') ||
                            sourceId.includes('highlight') ||
                            sourceId === 'counties' ||
                            sourceId === 'parcels') {
                            currentSources[sourceId] = this._map.getSource(sourceId).serialize();
                        }
                    });

                    // Change map style
                    this._map.setStyle(styleUrl);

                    // Re-add layers and sources after style loads
                    this._map.once('style.load', () => {
                        // Re-add sources
                        Object.keys(currentSources).forEach(sourceId => {
                            if (!this._map.getSource(sourceId)) {
                                this._map.addSource(sourceId, currentSources[sourceId]);
                            }
                        });

                        // Re-add layers in correct order: counties first, then parcels
                        // This ensures parcel points render on top of county fill
                        const countyLayers = currentLayers.filter(l => l.id.includes('counties'));
                        const parcelLayers = currentLayers.filter(l => l.id.includes('parcel'));
                        const otherLayers = currentLayers.filter(l => !l.id.includes('counties') && !l.id.includes('parcel'));
                        // Add in order: counties, other, parcels (parcels on top)
                        [...countyLayers, ...otherLayers, ...parcelLayers].forEach(layer => {
                            if (!this._map.getLayer(layer.id)) {
                                this._map.addLayer(layer);
                            }
                        });

                        // Apply visibility and opacity based on current zoom and style
                        const currentZoom = this._map.getZoom();

                        if (newStyle === 'satellite' && currentZoom >= 13.1) {
                            // Hide county layers
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'none');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'none');
                            }
                            // Reduce parcel points opacity
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.2);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        } else if (newStyle === 'satellite' && currentZoom < 13.1) {
                            // Show county layers, restore opacity
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        } else {
                            // Light view - show all, restore opacity
                            if (this._map.getLayer('counties-fill')) {
                                this._map.setLayoutProperty('counties-fill', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('counties-outline')) {
                                this._map.setLayoutProperty('counties-outline', 'visibility', 'visible');
                            }
                            if (this._map.getLayer('parcel-points')) {
                                try {
                                    this._map.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                                } catch (e) {
                                    console.warn('Could not set parcel-points opacity:', e);
                                }
                            }
                        }

                        // Re-apply paint properties to existing layers (like county fills)
                        Object.keys(layerPaintProperties).forEach(layerId => {
                            if (this._map.getLayer(layerId)) {
                                Object.keys(layerPaintProperties[layerId]).forEach(prop => {
                                    this._map.setPaintProperty(layerId, prop, layerPaintProperties[layerId][prop]);
                                });
                            }
                        });
                    });

                    setMapStyle(newStyle);
                };

                this._container.appendChild(this._button);
                return this._container;
            }

            onRemove() {
                this._container.parentNode.removeChild(this._container);
                this._map = undefined;
            }
        }

        mapRef.current.addControl(new StyleToggleControl(), 'top-right');

        // Add zoom listener to toggle county layers visibility
        mapRef.current.on('zoom', () => {
            const currentZoom = mapRef.current.getZoom();
            const currentStyle = mapRef.current.getStyle().sprite.includes('satellite') ? 'satellite' : 'light';

            // Debug: Log parcel-related layers
            const layers = mapRef.current.getStyle().layers;
            const parcelLayers = layers.filter(l => l.id.includes('parcel'));
            if (parcelLayers.length > 0) {
                // console.log('Parcel layers:', parcelLayers.map(l => l.id));
            }

            if (currentStyle === 'satellite' && currentZoom >= 13.1) {
                // Hide county layers
                if (mapRef.current.getLayer('counties-fill')) {
                    mapRef.current.setLayoutProperty('counties-fill', 'visibility', 'none');
                }
                if (mapRef.current.getLayer('counties-outline')) {
                    mapRef.current.setLayoutProperty('counties-outline', 'visibility', 'none');
                }
                // Reduce parcel points opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.3);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            } else if (currentStyle === 'satellite' && currentZoom < 13.1) {
                // Show county layers
                if (mapRef.current.getLayer('counties-fill')) {
                    mapRef.current.setLayoutProperty('counties-fill', 'visibility', 'visible');
                }
                if (mapRef.current.getLayer('counties-outline')) {
                    mapRef.current.setLayoutProperty('counties-outline', 'visibility', 'visible');
                }
                // Restore parcel points opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            } else if (currentStyle === 'light') {
                // Light view - restore full opacity
                if (mapRef.current.getLayer('parcel-points')) {
                    try {
                        mapRef.current.setPaintProperty('parcel-points', 'fill-opacity', 0.8);
                    } catch (e) {
                        console.warn('Could not set parcel-points opacity:', e);
                    }
                }
            }
        });

        mapRef.current.on("load", async () => {

            class ResetViewControl {
                constructor({ initialCenter, initialZoom, popupRef, customPopupRef }) {
                    this.initialCenter = initialCenter;
                    this.initialZoom = initialZoom;
                    this.popupRef = popupRef;
                    this.customPopupRef = customPopupRef;
                }

                onAdd(map) {
                    this.map = map;

                    this.button = document.createElement("button");
                    this.button.className = "mapboxgl-ctrl-icon mapboxgl-ctrl-reset";
                    this.button.type = "button";
                    this.button.title = "Reset View";
                    this.button.innerHTML = "&#x21BB;";

                    this.button.addEventListener("click", () => {
                        this.map.flyTo({
                            center: this.initialCenter,
                            zoom: this.initialZoom,
                            essential: true,
                        });


                        setSelectedCounty("chatham");
                        // setSelectedCounty("All");


                    });

                    this.container = document.createElement("div");
                    this.container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
                    this.container.appendChild(this.button);

                    return this.container;
                }

                onRemove() {
                    this.container.parentNode.removeChild(this.container);
                    this.map = undefined;
                }
            }



            // 1. Counties will be loaded from vector tiles after map is ready
            // We'll query the vector tiles after they load to extract county names and centers


            // Add custom legend control (bottom-left, horizontal)
            class LegendControl {
                onAdd(map) {
                    this._map = map;
                    this._container = document.createElement('div');
                    this._container.className = 'mapboxgl-ctrl';
                    this._container.style.background = '#fff';
                    this._container.style.padding = '6px 10px';
                    this._container.style.borderRadius = '4px';
                    this._container.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.1)';
                    this._container.style.fontFamily = 'Inter, -apple-system, sans-serif';
                    this._container.style.fontSize = '11px';
                    this._container.style.display = 'none'; // Initially hidden
                    this._container.style.gap = '10px';
                    this._container.style.alignItems = 'center';

                    const zones = [
                        { code: 'X', color: '#4CAF50' },
                        { code: 'AE', color: '#FF9800' },
                        { code: 'VE', color: '#E91E63' },
                        { code: 'A', color: '#2196F3' },
                        { code: 'Other', color: '#9fa2e7' }
                    ];

                    zones.forEach(zone => {
                        const item = document.createElement('div');
                        item.style.display = 'flex';
                        item.style.alignItems = 'center';
                        item.style.gap = '4px';

                        const colorBox = document.createElement('div');
                        colorBox.style.width = '14px';
                        colorBox.style.height = '10px';
                        colorBox.style.background = zone.color;
                        colorBox.style.borderRadius = '2px';
                        colorBox.style.border = '1px solid rgba(0,0,0,0.15)';

                        const label = document.createElement('span');
                        label.textContent = zone.code;
                        label.style.fontWeight = '500';
                        label.style.fontSize = '10px';
                        label.style.color = '#000';


                        item.appendChild(colorBox);
                        item.appendChild(label);
                        this._container.appendChild(item);
                    });

                    return this._container;
                }

                onRemove() {
                    this._container.parentNode.removeChild(this._container);
                    this._map = undefined;
                }
            }

            const legendControl = new LegendControl();
            mapRef.current.addControl(legendControl, 'bottom-left');
            legendControlRef.current = legendControl;

            //  Reset Control
            const resetControl = new ResetViewControl({
                initialCenter: NC_CENTER,
                initialZoom: 6.5,
                // popupRef: popupRef,
                // customPopupRef: customPopupRef
            });

            mapRef.current.addControl(resetControl, 'bottom-right');


            // Add zoom controls (bottom-right)
            mapRef.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

            // 2. Add County Source + Layers using vector tiles
            if (!mapRef.current.getSource("nc-counties")) {
                mapRef.current.addSource("nc-counties", {
                    type: "vector",
                    //tiles: ["http://localhost:8080/data/nc_counties/{z}/{x}/{y}.pbf"]
                    tiles: ["https://demo.flowlogic.com/tilesgl/data/nc_counties/{z}/{x}/{y}.pbf"]
                });
            }

            if (!mapRef.current.getLayer("counties-fill")) {
                mapRef.current.addLayer({
                    id: "counties-fill",
                    type: "fill",
                    source: "nc-counties",
                    "source-layer": "counties", // You may need to adjust this based on your MBTiles layer name
                    paint: {
                        "fill-color": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            "#42A5F5",  // Lighter blue for selected county
                            "#1565C0"   // Default blue for other counties
                        ],
                        "fill-opacity": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            0.5,   // Higher opacity for selected county
                            0.25   // Default opacity for other counties
                        ],
                    },
                });
            }

            if (!mapRef.current.getLayer("counties-outline")) {
                mapRef.current.addLayer({
                    id: "counties-outline",
                    type: "line",
                    source: "nc-counties",
                    "source-layer": "counties", // You may need to adjust this based on your MBTiles layer name
                    paint: {
                        "line-color": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            "#1976D2",  // Brighter blue for selected county
                            "#0D47A1"   // Default dark blue for other counties
                        ],
                        "line-width": [
                            "case",
                            ["==", ["downcase", ["get", "County"]], ["literal", selectedCounty || ""]],
                            2.5,   // Thicker line for selected county
                            1.2    // Default line width for other counties
                        ],
                    },
                });
            }

            // Add non-interactive county overlay layer
            if (!mapRef.current.getLayer("counties-non-interactive")) {
                mapRef.current.addLayer({
                    id: "counties-non-interactive",
                    type: "fill",
                    source: "nc-counties",
                    "source-layer": "counties",
                    paint: {
                        "fill-color": "#cccccc",
                        "fill-opacity": .5,
                    },
                    layout: {
                        visibility: "visible",
                    },
                    // Filter to show all counties EXCEPT Wake and Chatham
                    filter: [
                        "!",
                        [
                            "in",
                            ["downcase", ["get", "County"]],
                            ["literal", ["wake", "chatham"]]
                        ]
                    ]
                });
            }

            // Wait for tiles to load, then query features to extract county names and centers
            mapRef.current.once('idle', () => {
                try {
                    // Query all features from the counties layer
                    const features = mapRef.current.querySourceFeatures('nc-counties', {
                        sourceLayer: 'counties' // Adjust this if your layer name is different
                    });

                    console.log('Queried', features.length, 'county features from vector tiles');

                    if (features.length === 0) {
                        console.warn('No county features found - check source-layer name');
                        return;
                    }

                    // Extract county names for dropdown (sorted alphabetically)
                    const names = [...new Set(features.map(f => f.properties.County))].filter(Boolean).sort();
                    setCountiesList(names);

                    // Calculate county centers from feature geometries
                    const centers = {};
                    const countyFeatures = {};

                    // Group features by county
                    features.forEach(f => {
                        const countyName = f.properties.County;
                        if (!countyName) return;

                        if (!countyFeatures[countyName]) {
                            countyFeatures[countyName] = [];
                        }
                        countyFeatures[countyName].push(f);
                    });

                    // Calculate center for each county
                    Object.keys(countyFeatures).forEach(countyName => {
                        const countyParts = countyFeatures[countyName];
                        let totalX = 0, totalY = 0, pointCount = 0;

                        countyParts.forEach(feature => {
                            if (feature.geometry && feature.geometry.type === 'Polygon') {
                                const coords = feature.geometry.coordinates[0];
                                coords.forEach(coord => {
                                    totalX += coord[0];
                                    totalY += coord[1];
                                    pointCount++;
                                });
                            } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
                                feature.geometry.coordinates.forEach(polygon => {
                                    polygon[0].forEach(coord => {
                                        totalX += coord[0];
                                        totalY += coord[1];
                                        pointCount++;
                                    });
                                });
                            }
                        });

                        centers[countyName.toLowerCase()] = pointCount ? [totalX / pointCount, totalY / pointCount] : NC_CENTER;
                    });

                    setCountyCenters(centers);
                    console.log('Loaded', names.length, 'counties from vector tiles:', names.join(', '));
                } catch (error) {
                    console.error('Error querying county features:', error);
                }
            });

            // 3. Add Parcels vector source & layer but hidden initially
            if (!mapRef.current.getSource("parcels")) {
                mapRef.current.addSource("parcels", {
                    type: "vector",
                    //tiles: ["http://demo.flowlogic.com:8080/data/ncarolina/{z}/{x}/{y}.pbf"],
                    // tiles: ["https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    //tiles: ["http://localhost:8080/data/ncarolina/{z}/{x}/{y}.pbf"],
                    
                    // tiles: [adjacentMode ? "http://localhost:8007/data/adjparcels/{z}/{x}/{y}.pbf" : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                    // tiles: [adjacentMode ? "https://demo.flowlogic.com/tilesgl/data/adjparcels/{z}/{x}/{y}.pbf" : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                     tiles :[ adjacentMode ? 
   
             adjacentParcelFilter === "Suitable Parcels" 
           

            ? "https://demo.flowlogic.com/tilesgl/data/adjparcelsfinal/{z}/{x}/{y}.pbf"
            : "https://demo.flowlogic.com/tilesgl/data/adjparcelsrejected/{z}/{x}/{y}.pbf"

            : "https://demo.flowlogic.com/tilesgl/data/ncarolina/{z}/{x}/{y}.pbf"],
                });
            }

            if (!mapRef.current.getLayer("parcel-points")) {
                mapRef.current.addLayer({
                    id: "parcel-points",
                    type: "fill",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "fill-color": [
                            "match",
                            ["get", "parcelcustomfemaflood"],
                            "X",
                            "#4CAF50",
                            "AE",
                            "#FF9800",
                            "VE",
                            "#E91E63",
                            "A",
                            "#2196F3",
                            "#9fa2e7",
                        ],
                        "fill-opacity": 0.45,
                    },
                });
            }

            // Add parcel outline layer with orange color at zoom >= 13
            if (!mapRef.current.getLayer("parcel-outline")) {
                mapRef.current.addLayer({
                    id: "parcel-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "line-color": [
                            "step",
                            ["zoom"],
                            "#333",      // Dark gray below zoom 13
                            13, "#ebeaea"  // Orange at zoom 13 and above
                        ],
                        "line-width": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            0, 0.5,      // Thin at low zoom
                            13, 1.0,     // Medium at zoom 13
                            18, 2.0      // Thicker at high zoom
                        ],
                        "line-opacity": [
                            "step",
                            ["zoom"],
                            0,           // Invisible below zoom 13
                            13, 0.9      // Visible at zoom 13 and above
                        ]
                    },
                });
            }

            // Add parcel hover highlight layer (red border on mouseover)
            if (!mapRef.current.getLayer("parcel-hover")) {
                mapRef.current.addLayer({
                    id: "parcel-hover",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "line-color": "#ff7300",  // Red color
                        // FF0000
                        "line-width": 3,
                        "line-opacity": 0
                    },
                    filter: ["==", ["get", "parcel"], ""] // Initially show nothing
                });
            }

            // Selected parcels fill for click highlight

            // if (!mapRef.current.getLayer("parcel-selected-fill")) {
            //     mapRef.current.addLayer({
            //         id: "parcel-selected-fill",
            //         type: "fill",
            //         source: "parcels",
            //         "source-layer": "fema",
            //         layout: { visibility: "none" },
            //         paint: {
            //             "fill-color": "#b8eee6",
            //             "fill-opacity": 0.4
            //         },
            //         filter: ["in", ["get", "parcel"], ["literal", []]]
            //     });
            // }

            // Selected parcels outline

            if (!mapRef.current.getLayer("parcel-selected-outline")) {
                mapRef.current.addLayer({
                    id: "parcel-selected-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "none" },
                    paint: {
                        "line-color": "#fd0224",
                        "line-width": 2.5,
                        "line-opacity": 0.95
                    },
                    // filter: ["in", ["get", "parcel"], ["literal", []]]
                    filter: ["==", ["get", "parcel"], ""],
                    //  filter: ["in", "parcel", ""]
                });
            }

            // 4. Ensure counties above parcels
            mapRef.current.on("idle", () => {
                try {
                    mapRef.current.moveLayer("counties-fill", "parcel-points");
                    mapRef.current.moveLayer("counties-outline", "parcel-points");
                } catch (e) { }
            });

            // Debug: verify parcels source-layer returns features
            mapRef.current.once('idle', () => {
                try {
                    const feats = mapRef.current.querySourceFeatures('parcels', { sourceLayer: 'fema' }) || [];
                    console.log('Parcels sourceLayer=fema features available:', feats.length);
                } catch (e) { console.warn('Could not query parcels source features. Check source-layer name.', e); }
            });



            mapRef.current.on("click", "counties-fill", e => {
                const feature = e.features?.[0];
                if (!feature) return;

                const countyName = String(feature.properties.County || "").toLowerCase();

                // Skip if clicking the same county that's already selected
                if (selectedCountyRef.current === countyName) {
                    console.log(`County "${countyName}" is already selected, skipping reload`);
                    return;
                }

                // Only update panel / selected county for supported counties
                if (countyName === "chatham" || countyName === "wake") {
                    updateLeftPanelParcels(countyName);
                    setSelectedCounty(countyName);
                    //fetchTotalCount(countyName); // Fetch total count for the county
                } else {
                    console.log(`County "${countyName}" clicked but not in [chatham, wake]; skipping panel update`);
                }

                boundsInitialized.current = false;
                vacantFilterTouched.current = false;
                zoneFilterTouched.current = false;
                listedSaleFilterTouched.current = false;

                // Clear checkbox filters when county changes
                setZoneCodeFilter([]);
                setFloodRiskFilter("All");
                setListedSaleFilter("All");
                setVacantFilter("All");

                //  SAFELY compute center from Polygon / MultiPolygon
                let bounds = new mapboxgl.LngLatBounds();

                const geom = feature.geometry;

                if (geom.type === "Polygon") {
                    geom.coordinates[0].forEach(coord => bounds.extend(coord));
                } else if (geom.type === "MultiPolygon") {
                    geom.coordinates.forEach(polygon => {
                        polygon[0].forEach(coord => bounds.extend(coord));
                    });
                }

                const center = bounds.getCenter();

                if (!Number.isFinite(center.lng) || !Number.isFinite(center.lat)) {
                    console.warn(" Invalid county center:", feature);
                    return;
                }

                const screenWidth = window.innerWidth;
                // const leftPanelWidth = screenWidth * 0.6;
                const leftPanelWidth = screenWidth * (listWidth / 100);
                const offset = -(leftPanelWidth / 2);

                // Only animate flyTo for supported counties
                if (countyName === "chatham" || countyName === "wake") {
                    mapRef.current.flyTo({
                        center: [center.lng, center.lat],
                        zoom: 9,
                        speed: 0.8,
                        offset: [offset, 0],
                    });
                }
            });



            // 6. Parcel click → zoom and show detailed popup (only at zoom > 13.1)
            mapRef.current.on("click", "parcel-points", (e) => {
                const p = e.features && e.features[0] && e.features[0].properties;
                if (!p) return;

                // Only trigger zoom/popup if zoom level > 13.1
                const currentZoom = mapRef.current.getZoom();
                // if (currentZoom >= 13.1 && adjacentMode == false) {
                if (currentZoom >= 13.1 && adjacentModeRef.current === false) {
                    // Use the same handler as row clicks for consistent behavior
                    handleRowClick(p);
                }
            });

            // Track hovered parcel with mousemove for better adjacent parcel handling
            let hoveredParcelId = null;

            mapRef.current.on("mousemove", "parcel-points", (e) => {
                if (mapRef.current.getZoom() <= 13.1) return;

                const feature = e.features?.[0];
                if (!feature?.properties) return;

                const p = feature.properties;
                const parcelId = p.parcel || p.parcelid;

                if (!parcelId) return;

                // Highlight parcel
                if (mapRef.current.getLayer("parcel-hover")) {
                    mapRef.current.setFilter("parcel-hover", ["==", ["get", "parcel"], parcelId]);
                    mapRef.current.setPaintProperty("parcel-hover", "line-opacity", 1);
                }

                //     const parcelHTML = adjacentMode ? `
                // <strong>Parcel Area:</strong>
                // ${p.arealotsf ? `${p.arealotsf} sq ft` : "N/A"}<br>

                // <strong>Building Area:</strong>
                // ${p.areabuilding ? `${p.areabuilding} sq ft` : "N/A"}<br>
                // ` : '';

    
                // Remove previous hover popup ONLY
                hoverPopupRef.current?.remove();

                hoverPopupRef.current = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false
                })
                
                
                    .setLngLat(e.lngLat)
                    .setHTML(`
        <div style="
            background:#fff;
            padding:10px;
            border-radius:6px;
            box-shadow:0 2px 6px rgba(0,0,0,0.15);
            font-family:Inter, sans-serif;
            font-size:12px;
            width:240px;
        ">
            <strong>Parcel ID:</strong>
            ${p.parcel || p.parcelid || "N/A"}<br>
 
            <strong>County:</strong>
            ${p.county || "N/A"}<br>
 
            <strong>Address:</strong>
            ${p.addrline || p.addrline1 || p.address || "N/A"}<br>
 
            <strong>Zoning:</strong>
            ${p.zonedcodelocal || p.ZonedCodeLocal || p.zoning || "N/A"}<br>
 
            <strong>Flood Zone:</strong>
            ${p.parcelcustomfemaflood || p.FloodFemaRisk || "N/A"}<br>

  
     ${adjacentModeRef.current ? `
    <div><strong>Parcel Area:</strong> ${p.arealotsf ? `${p.arealotsf} sq ft` : "N/A"}</div>
    <div><strong>Building Area:</strong> ${p.areabuilding ? `${p.areabuilding} sq ft` : "N/A"}</div>
` : ''}



        </div>
    `)
                    .addTo(mapRef.current);


                mapRef.current.getCanvas().style.cursor = "pointer";
            });


            mapRef.current.on("mouseleave", "parcel-points", () => {
                hoverPopupRef.current?.remove();
                hoverPopupRef.current = null;

                mapRef.current.getCanvas().style.cursor = "";

                // Hide red border
                if (mapRef.current.getLayer("parcel-hover")) {
                    mapRef.current.setPaintProperty("parcel-hover", "line-opacity", 0);
                }
            });

            // mapRef.current.on("zoom", () => {
            //     const zoom = mapRef.current.getZoom();

            //     if (zoom < 13.1) {
            //         clickPopupRef.current?.remove();
            //         clickPopupRef.current = null;
            //     }
            // });

            mapRef.current.on("zoom", () => {
                const zoom = mapRef.current.getZoom();

                const isAboveThreshold = zoom >= 13.1;

                //  ONLY clear popup when crossing from above → below
                if (wasAboveZoomThresholdRef.current && !isAboveThreshold) {
                    clickPopupRef.current?.remove();
                    clickPopupRef.current = null;
                }

                // Update previous state
                wasAboveZoomThresholdRef.current = isAboveThreshold;
            });

            setMapLoaded(true);
        });

        // return () => mapRef.current?.remove();

        return () => {
            mapRef.current?.remove();
            mapRef.current = null;
        };

    }, []);


    // Track zoom level changes
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        const handleZoomEnd = () => {
            const zoom = map.getZoom();
            setCurrentZoom(zoom);
        };

        // Set initial zoom
        setCurrentZoom(map.getZoom());

        map.on("zoomend", handleZoomEnd);

        return () => {
            map.off("zoomend", handleZoomEnd);
        };
    }, [mapLoaded]);

    // Update county highlighting when selectedCounty changes
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        // Update fill color and opacity for selected county
        if (map.getLayer("counties-fill")) {
            map.setPaintProperty("counties-fill", "fill-color", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                "#42A5F5",  // Lighter blue for selected county
                "#1565C0"   // Default blue for other counties
            ]);
            map.setPaintProperty("counties-fill", "fill-opacity", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                0.5,   // Higher opacity for selected county
                0.25   // Default opacity for other counties
            ]);
        }

        // Update outline color and width for selected county
        if (map.getLayer("counties-outline")) {
            map.setPaintProperty("counties-outline", "line-color", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                "#1976D2",  // Brighter blue for selected county
                "#0D47A1"   // Default dark blue for other counties
            ]);
            map.setPaintProperty("counties-outline", "line-width", [
                "case",
                ["==", ["downcase", ["get", "County"]], selectedCounty || ""],
                2.5,   // Thicker line for selected county
                1.2    // Default line width for other counties
            ]);
        }
    }, [selectedCounty, mapLoaded]);

    // Control legend visibility based on county selection
    useEffect(() => {
        if (legendControlRef.current && legendControlRef.current._container) {
            if (selectedCounty !== "all") {
                legendControlRef.current._container.style.display = 'flex';
            } else {
                legendControlRef.current._container.style.display = 'none';
            }
        }
    }, [selectedCounty]);

    // Add this useEffect to handle county changes in adjacent mode
useEffect(() => {
    if (adjacentMode && selectedCounty !== "all") {
        // Reload tiles when county changes in adjacent mode
        reloadParcelTiles(adjacentMode);
        
        // Reapply county filter after tile reload
        setTimeout(() => {
            if (mapRef.current && selectedCounty) {
                mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                const filterExpr = ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()];
                mapRef.current.setFilter("parcel-points", filterExpr);
                if (mapRef.current.getLayer("parcel-outline")) {
                    mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                    mapRef.current.setFilter("parcel-outline", filterExpr);
                }
                    if (mapRef.current.getLayer("parcel-selected-outline")) {
        mapRef.current.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
    }
                
    
            }
        }, 1000);
    }
}, [selectedCounty, adjacentMode]);


    //zoom boundary trigger track to invoke api call 
    useEffect(() => {
        const prevZoom = prevZoomRef.current;
        const currZoom = currentZoom;

        const crossedIntoFetchZone =
            prevZoom >= 13.1 && currZoom < 13.1;

        if (crossedIntoFetchZone) {
            // Check if any filters are active
            const hasActiveFilters =
                vacantFilterTouched.current ||
                zoneFilterTouched.current ||
                listedSaleFilterTouched.current ||
                (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
                (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
                (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
                (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread) ||
                zoneCodeFilter.length > 0;

            // Only trigger API fetch if no filters are applied
            // If filters are active, keep the existing filtered data
            if (!hasActiveFilters) {
                console.log(' Zoom crossed below 13.1 → trigger API fetch (no filters applied)');
                setZoomBoundaryTrigger(Date.now());
            } else {
                console.log(' Zoom crossed below 13.1 → skipping API fetch (filters applied, using existing data)');
            }
        }

        prevZoomRef.current = currZoom;
    }, [currentZoom, priceRange, zestimateRange, sizeRange, spreadRange, zoneCodeFilter, sliderMinPrice, sliderMaxPrice, sliderMinZestimate, sliderMaxZestimate, sliderMinSize, sliderMaxSize, sliderMinSpread, sliderMaxSpread]);

    useEffect(() => {
        if (!selectedCounty || selectedCounty === 'all') return;

        // Check if county actually changed from previous value
        const previousCounty = selectedCountyRef.current;
        if (previousCounty !== selectedCounty) {
            // County changed, reset the flag to allow re-initialization
            minMaxInitialized.current = false;
            selectedCountyRef.current = selectedCounty;
            console.log(' County changed from', previousCounty, 'to', selectedCounty, '- resetting minMaxInitialized');
        }

        // Skip min/max initialization since it's handled in the adjacentMode useEffect
        // Only handle listed sale options here
        if (apiParcels.length > 0) {
            const listedVals = apiParcels.map((p) => p.listedsale || p.listed_sale || "Not listed");
            const uniqueListed = Array.from(new Set(listedVals));
            setListedSaleOptions(uniqueListed.sort());
        }
    }, [selectedCounty, apiParcels]);





    // Apply filters to API parcels
    useEffect(() => {
        //  HARD GUARD — required
        if (!Array.isArray(apiParcels)) {
            console.warn("apiParcels is not an array:", apiParcels);
            setFilteredApiParcels([]);
            return;
        }

        if (apiParcels.length === 0) {
            setFilteredApiParcels([]);
            return;
        }

        console.log(
            "Client-side filtering API parcels:",
            apiParcels.length,
            "parcels"
        );

        // const filtered = apiParcels.filter(() => true);

        let filtered = apiParcels.map(p => ({ ...p }));

        if (adjacentMode) {
            const seen = new Set();
            filtered = filtered.filter(p => {
                const key = `${p.parcel}-${p.touching_parcel || p.adjacent_parcel}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        console.log(
            "Client-side filter result:",
            filtered.length,
            "parcels (filtered from",
            apiParcels.length,
            ")"
        );

        setFilteredApiParcels(filtered);
    }, [apiParcels]);

    // Callback when price range slider drag is complete
    const handlePriceRangeChangeComplete = (newRange) => {
        console.log('Price range drag complete:', newRange);
        setPriceRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when zestimate range slider drag is complete
    const handleZestimateRangeChangeComplete = (newRange) => {
        console.log('Zestimate range drag complete:', newRange);
        setZestimateRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when size range slider drag is complete
    const handleSizeRangeChangeComplete = (newRange) => {
        console.log('Size range drag complete:', newRange);
        setSizeRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Callback when spread range slider drag is complete
    const handleSpreadRangeChangeComplete = (newRange) => {
        console.log('Spread range drag complete:', newRange);
        setSpreadRangeDragComplete(Date.now()); // Update timestamp to trigger effect
    };

    // Handle sorting changes - trigger API call when sort options change
    useEffect(() => {
        if (!selectedCounty || selectedCounty === "all") return;
        
        console.log("🔄 Sort changed - sortBy:", sortBy, "sortOrder:", sortOrder);
        
        // Check if any filters are active
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

        if (hasActiveFilters || currentZoom < 13.1) {
            // Call API with current filters and new sort parameters
            fetchApiParcels(
                selectedCounty,
                priceRange[0] !== sliderMinPrice ? priceRange[0] : null,
                priceRange[1] !== sliderMaxPrice ? priceRange[1] : null,
                zestimateRange[0] !== sliderMinZestimate ? zestimateRange[0] : null,
                zestimateRange[1] !== sliderMaxZestimate ? zestimateRange[1] : null,
                sizeRange[0] !== sliderMinSize ? sizeRange[0] : null,
                sizeRange[1] !== sliderMaxSize ? sizeRange[1] : null,
                spreadRange[0] !== sliderMinSpread ? spreadRange[0] : null,
                spreadRange[1] !== sliderMaxSpread ? spreadRange[1] : null,
                zoneCodeFilter.length > 0 ? zoneCodeFilter : null,
                vacantFilter !== "All" ? vacantFilter : null,
                listedSaleFilter !== "All" ? listedSaleFilter : null,
                floodRiskFilter !== "All" ? floodRiskFilter : null,
                sortBy,
                sortOrder,
                municipalityFilter !== "All" ? municipalityFilter : null,
                0
            );
        }
    }, [sortBy, sortOrder]);

    // Consolidated API call effect - combines all active filters (price, zestimate, size, spread, zone)
    // Triggers when any filter's drag complete timestamp changes and zoom < 13.1
    useEffect(() => {
        console.log("@@@***inside useeffect ",sortOrder )


        // Check if any filters are active (including municipality)
        const hasActiveFilters =
            (vacantFilter && vacantFilter !== "All") ||
            (zoneCodeFilter && zoneCodeFilter.length > 0) ||
            (listedSaleFilter && listedSaleFilter !== "All") ||
            (floodRiskFilter && floodRiskFilter !== "All") ||
            (municipalityFilter && municipalityFilter !== "All") ||
            (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
            (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
            (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
            (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);



        console.log(' API Effect triggered - selectedCounty:', selectedCounty, 'zoom:', currentZoom);

        // Skip API call if county just changed but no filters are active, UNLESS filters were just cleared
        // This prevents duplicate calls when min/max initialization updates range states

        // if (selectedCounty && !hasActiveFilters && !filtersClearedRef.current) {
        //     console.log(' Skipping fetchApiParcels (county changed but no filters active)');
        //     return;
        // }

        const isZoneReset =
    zoneFilterTouched.current &&
    zoneCodeFilter.length === 0;

const isFloodReset =
    floodRiskFilterTouched.current &&
    floodRiskFilter === "All";

const isFilterReset = isZoneReset || isFloodReset;

if (
    selectedCounty &&
    !hasActiveFilters &&
    !filtersClearedRef.current &&
    !isFilterReset
) {
    console.log(' Skipping fetchApiParcels (county changed but no filters active)');
    return;
}

        // Reset the filters cleared flag after checking
        if (filtersClearedRef.current) {
            filtersClearedRef.current = false;
            console.log(' Filters were cleared - allowing API call');
        }

        if (isParcelSearchActive && !hasActiveFilters) {
            console.log(" Skipping fetchApiParcels (parcel search active)");
            return;
        }

        if (selectedCounty === "all" || !selectedCounty) {
            console.log(' Skipping: No county selected');
            return;
        }

        // Check if zone code filter is active (dropdown, not slider)
        const isZoneCodeFilterActive = zoneCodeFilter && zoneCodeFilter.length > 0;

        // Check if vacant filter is active (checkbox, not slider)
        const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

        const isMunicipalityFilterActive =
            municipalityFilter && municipalityFilter !== "All";

        if (isMunicipalityFilterActive) {
            municipalityFilterTouched.current = true;
        }


        // Track when filters are touched
        if (isZoneCodeFilterActive) {
            zoneFilterTouched.current = true;
        }
        if (isVacantFilterActive) {
            vacantFilterTouched.current = true;
        }
        if (listedSaleFilter && listedSaleFilter !== "All") {
            listedSaleFilterTouched.current = true;
        }
        if (floodRiskFilter  && floodRiskFilter !== "All") {
    floodRiskFilterTouched.current = true;
}

        console.log(' Filter states:', {
            vacantFilter,
            isVacantFilterActive,
            vacantFilterTouched: vacantFilterTouched.current,
            listedSaleFilter,
            isListedSaleFilterActive: listedSaleFilter && listedSaleFilter !== "All",
            listedSaleFilterTouched: listedSaleFilterTouched.current,
            zoneCodeFilter,
            isZoneCodeFilterActive,
            zoneFilterTouched: zoneFilterTouched.current,
            dragStates: {
                price: priceRangeDragComplete,
                zestimate: zestimateRangeDragComplete,
                size: sizeRangeDragComplete,
                spread: spreadRangeDragComplete
            }
        });

        // Skip ONLY on initial load when no filters have ever been touched
        // We trigger API calls when:
        // 1. Any slider has been dragged at least once (timestamps > 0), OR
        // 2. Any dropdown/checkbox filter is currently active (not "All"), OR
        // 3. Any dropdown/checkbox filter was previously active (touched refs), OR
        // 4. Sorting is active (not default values)
        const hasAnySliderBeenDragged = priceRangeDragComplete !== 0 || zestimateRangeDragComplete !== 0 ||
            sizeRangeDragComplete !== 0 || spreadRangeDragComplete !== 0;


        const isAnyDropdownFilterActive = isZoneCodeFilterActive || isVacantFilterActive || (listedSaleFilter && listedSaleFilter !== "All") || (floodRiskFilter && floodRiskFilter !== "All");
        //   const isAnyDropdownFilterActive = isZoneCodeFilterActive || isVacantFilterActive || (listedSaleFilter && listedSaleFilter !== "All") || (floodRiskFilter && floodRiskFilter !== "All")|| isMunicipalityFilterActive;

        const hasAnyDropdownBeenTouched =
            zoneFilterTouched.current ||
            vacantFilterTouched.current ||
            listedSaleFilterTouched.current;
        // ||  municipalityFilterTouched.current;


        const isSortingActive = sortBy !== null; // Always allow API calls when sorting is specified


        // Check if ANY filter (other than county) is active
        const hasAnyFilterActive = hasAnySliderBeenDragged || isAnyDropdownFilterActive || hasAnyDropdownBeenTouched || isSortingActive;

        if (!hasAnyFilterActive) {

            console.log('Skipping: No filters activated yet');
            return;
        }


        // Only call API with filters when zoom < 13.1
        // if (currentZoom < 13.1) {
        if (currentZoom < 13.1 || hasAnyFilterActive) {
            console.log(' Zoom < 13.1, checking filters...');


            // Check which filters are active using small absolute tolerances (for log scale)
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > 0.01);

            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > 0.1 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > 0.1);

            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > 0.01);

            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > 0.1 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > 0.1);

            console.log(' Active Filters:', {
                price: isPriceFilterActive,
                zestimate: isZestimateFilterActive,
                size: isSizeFilterActive,
                spread: isSpreadFilterActive,
                zoneCode: isZoneCodeFilterActive,
                vacant: isVacantFilterActive
            });

            // Always call API - pass null for inactive filters to get unfiltered data
            // Prepare filter parameters - pass null for inactive filters
            const landValueMin = isPriceFilterActive ? priceRange[0] : null;
            const landValueMax = isPriceFilterActive ? priceRange[1] : null;
            const zestimateMin = isZestimateFilterActive ? zestimateRange[0] : null;
            const zestimateMax = isZestimateFilterActive ? zestimateRange[1] : null;
            const parcelsizeMin = isSizeFilterActive ? sizeRange[0] : null;
            const parcelsizeMax = isSizeFilterActive ? sizeRange[1] : null;
            const spreadMin = isSpreadFilterActive ? spreadRange[0] : null;
            const spreadMax = isSpreadFilterActive ? spreadRange[1] : null;
            const zoneCode = isZoneCodeFilterActive && zoneCodeFilter.length > 0 ? zoneCodeFilter.join(',') : null;
            // When vacant filter is active (not "All"), send is_vacant=1 to show only vacant parcels
            const isVacant = isVacantFilterActive ? 1 : null;
            // When listed sale filter is active (not "All"), send listedsale=all
            const listedSale = (listedSaleFilter && listedSaleFilter !== "All") ? "all" : null;
            // When flood risk filter is active (not "All"), send flood_risk value
            const floodRisk = (floodRiskFilter && floodRiskFilter !== "All") ? floodRiskFilter : null;

            const municipality =
                municipalityFilter !== "All" ? municipalityFilter : null;

            setCurrentOffset(0);
            setHasMoreData(true);
            setIsLoadingMore(false);


            // Call API with all active filters
            console.log(' Calling API with filters - spread:', spreadMin, spreadMax, 'zone:', zoneCode, 'is_vacant:', isVacant, 'flood:', floodRisk, 'sort:', sortBy, sortOrder);
            fetchApiParcels(
                selectedCounty,
                landValueMin,
                landValueMax,
                zestimateMin,
                zestimateMax,
                parcelsizeMin,
                parcelsizeMax,
                spreadMin,
                spreadMax,
                zoneCode,
                isVacant,
                listedSale,
                floodRisk,
                sortBy,
                sortOrder,
                municipality,
                0
            );
        }
    }, [
        priceRangeDragComplete,
        zestimateRangeDragComplete,
        sizeRangeDragComplete,
        spreadRangeDragComplete,
        zoneCodeFilter,
        municipalityFilter,
        vacantFilter,
        listedSaleFilter,
        floodRiskFilter,
        sortBy,
        sortOrder,
        // currentZoom,
        selectedCounty,
        zoomBoundaryTrigger,
        // zoneCodes
    ]);

    // Consolidated map tile filter effect - combines all active filters (price, zestimate, size)
    // Applies filters to map layers at all zoom levels
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
          if (adjacentMode) return; 
        const map = mapRef.current;

        // Apply filtering when county is selected
        if (selectedCounty !== "all") {
            // Check which filters are active
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

            const isZoneCodeFilterActive = zoneCodeFilter && zoneCodeFilter.length > 0;

            const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

            // Build combined filter starting with county
            const filter = [
                "all",
                ["==", ["downcase", ["get", "county"]], selectedCounty.toLowerCase()]
            ];

            // Add price filter if active
            if (isPriceFilterActive) {
                filter.push(
                    ["has", "totallandvalue"],
                    ["!=", ["get", "totallandvalue"], null],
                    [">=", ["to-number", ["get", "totallandvalue"], 0], priceRange[0]],
                    ["<=", ["to-number", ["get", "totallandvalue"], 0], priceRange[1]]
                );
            }

            // Add zestimate filter if active
            if (isZestimateFilterActive) {
                filter.push(
                    ["has", "zestimate"],
                    ["!=", ["get", "zestimate"], null],
                    [">=", ["to-number", ["get", "zestimate"], 0], zestimateRange[0]],
                    ["<=", ["to-number", ["get", "zestimate"], 0], zestimateRange[1]]
                );
            }

            // Add size filter if active
            if (isSizeFilterActive) {
                filter.push(
                    ["has", "parcelsize"],
                    ["!=", ["get", "parcelsize"], null],
                    [">=", ["to-number", ["get", "parcelsize"], 0], sizeRange[0]],
                    ["<=", ["to-number", ["get", "parcelsize"], 0], sizeRange[1]]
                );
            }

            // Add spread filter if active
            if (isSpreadFilterActive) {
                filter.push(
                    ["has", "spreadpercentage"],
                    ["!=", ["get", "spreadpercentage"], null],
                    [">=", ["to-number", ["get", "spreadpercentage"], 0], spreadRange[0]],
                    ["<=", ["to-number", ["get", "spreadpercentage"], 0], spreadRange[1]]
                );
            }

            // Add zone code filter if active
            if (isZoneCodeFilterActive) {
                filter.push(
                    ["has", "zonedcodelocal"],
                    ["in", ["downcase", ["get", "zonedcodelocal"]], ["literal", zoneCodeFilter.map(z => z.toLowerCase())]]
                );
            }

            // Add vacant filter if active
            if (isVacantFilterActive) {
                filter.push(
                    ["has", "is_vaccant"],
                    ["==", ["to-number", ["get", "is_vaccant"], 0], 1]
                );
            }

            // Add listed sale filter if active
            const isListedSaleFilterActive = listedSaleFilter && listedSaleFilter !== "All";
            if (isListedSaleFilterActive) {
                filter.push(
                    ["has", "listedsale"],
                    [
                        "any",
                        ["==", ["get", "listedsale"], "PendingSale"],
                        ["==", ["get", "listedsale"], "ActiveListing"]
                    ]
                );
            }

            // Add flood risk filter if active
            const isFloodFilterActive = floodRiskFilter && floodRiskFilter !== "All";
            // if (isFloodFilterActive) {
            if (isFloodFilterActive && !adjacentMode) {
                filter.push(
                    ["has", "parcelcustomfemaflood"],
                    ["==", ["get", "parcelcustomfemaflood"], floodRiskFilter]
                );
            }

            // Add municipality filter if active
            const isMunicipalityFilterActive =
                municipalityFilter && municipalityFilter !== "All";

            if (isMunicipalityFilterActive) {
                filter.push(
                    ["has", "municipality"],
                    ["==", ["downcase", ["get", "municipality"]], municipalityFilter.toLowerCase()]
                );
            }

            // Check if ANY filter is active (excluding county which is always there)
            const hasAnyActiveFilter = isPriceFilterActive || isZestimateFilterActive ||
                isSizeFilterActive || isSpreadFilterActive ||
                isZoneCodeFilterActive || isVacantFilterActive ||
                isListedSaleFilterActive || isFloodFilterActive;

            console.log('🗺️ Map Filter Update:', {
                isZestimateActive: isZestimateFilterActive,
                zestimateRange,
                sliderMinZestimate,
                sliderMaxZestimate,
                tolerance: Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01
            });

            // Apply the combined filter to all parcel layers
            if (map.getLayer("parcel-fill")) map.setFilter("parcel-fill", filter);
            if (map.getLayer("parcel-outline")) map.setFilter("parcel-outline", filter);
            if (map.getLayer("parcel-points")) map.setFilter("parcel-points", filter);
            if (map.getLayer("parcel-hover")) map.setFilter("parcel-hover", filter);
        } else {
            // No county selected - hide all parcels
            const hideAllFilter = ["==", ["get", "county"], ""];
            if (map.getLayer("parcel-fill")) map.setFilter("parcel-fill", hideAllFilter);
            if (map.getLayer("parcel-outline")) map.setFilter("parcel-outline", hideAllFilter);
            if (map.getLayer("parcel-points")) map.setFilter("parcel-points", hideAllFilter);
            if (map.getLayer("parcel-hover")) map.setFilter("parcel-hover", hideAllFilter);
        }
    }, [
        adjacentMode,
        priceRange,
        zestimateRange,
        municipalityFilter,
        sizeRange,
        spreadRange,
        zoneCodeFilter,
        vacantFilter,
        listedSaleFilter,
        floodRiskFilter,
        selectedCounty,
        sliderMinPrice,
        sliderMaxPrice,
        sliderMinZestimate,
        sliderMaxZestimate,
        sliderMinSize,
        sliderMaxSize,
        sliderMinSpread,
        sliderMaxSpread,
        mapLoaded
    ]);

    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        const map = mapRef.current;

        const updateVisibleParcels = () => {
            // Only update visible parcels when zoom >= 13.1
            const currentZoom = map.getZoom();
            if (currentZoom < 13.1) {
                console.log("Zoom < 13.1, not querying tile parcels");
                return;
            }

            // Only update if the layer exists and is visible
            if (!map.getLayer("parcel-points")) {
                console.log("parcel-points layer not ready yet");
                return;
            }

            const visibility = map.getLayoutProperty("parcel-points", "visibility");
            if (visibility === "none") {
                console.log("parcel-points layer is hidden");
                setFilteredParcels([]);
                return;
            }

            // Query features rendered in viewport (includes buffer tiles)
            const featuresInView = map.queryRenderedFeatures({ layers: ["parcel-points"] });
            console.log(`Raw features from queryRenderedFeatures: ${featuresInView.length}`);

            // Get actual visible viewport bounds to filter out buffer tiles
            const bounds = map.getBounds();

            // Filter to only include features actually within visible bounds
            const filteredFeatures = featuresInView.filter(f => {
                const props = f.properties;
                const lon = parseFloat(props.longitude || props.lon || props.lng || props.x);
                const lat = parseFloat(props.latitude || props.lat || props.y);

                if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                    return false; // Skip features without valid coordinates
                }

                // Check if coordinates are within visible bounds
                return bounds.contains([lon, lat]);
            });

            console.log(`After bounds filtering: ${filteredFeatures.length} features`);

            // Deduplicate features by parcel ID (or other unique identifier)
            const uniqueFeatures = new Map();
            filteredFeatures.forEach(f => {
                const props = f.properties;
                const uniqueId = props.parcel || props.parcelid || props.geo_id || props.id;
                if (uniqueId && !uniqueFeatures.has(uniqueId)) {
                    uniqueFeatures.set(uniqueId, props);
                }
            });

            let parcelsArray = Array.from(uniqueFeatures.values());

            // Apply Price (zestimate) filter if active
            const isZestimateFilterActive = sliderMinZestimate !== undefined && sliderMaxZestimate !== undefined &&
                (Math.abs(zestimateRange[0] - sliderMinZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01 ||
                    Math.abs(zestimateRange[1] - sliderMaxZestimate) > Math.abs(sliderMaxZestimate - sliderMinZestimate) * 0.01);

            if (isZestimateFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const zest = parseFloat(p.zestimate) || 0;
                    return zest >= zestimateRange[0] && zest <= zestimateRange[1];
                });
                console.log(`After Price filter: ${parcelsArray.length} parcels (range: ${zestimateRange[0]}-${zestimateRange[1]})`);
            }

            // Apply Dev Value (totallandvalue) filter if active
            const isPriceFilterActive = sliderMinPrice !== undefined && sliderMaxPrice !== undefined &&
                (Math.abs(priceRange[0] - sliderMinPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01 ||
                    Math.abs(priceRange[1] - sliderMaxPrice) > Math.abs(sliderMaxPrice - sliderMinPrice) * 0.01);

            if (isPriceFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const landValue = parseFloat(p.totallandvalue) || 0;
                    return landValue >= priceRange[0] && landValue <= priceRange[1];
                });
                console.log(`After Dev Value filter: ${parcelsArray.length} parcels (range: ${priceRange[0]}-${priceRange[1]})`);
            }

            // Apply Size (parcelsize) filter if active
            const isSizeFilterActive = sliderMinSize !== undefined && sliderMaxSize !== undefined &&
                (Math.abs(sizeRange[0] - sliderMinSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01 ||
                    Math.abs(sizeRange[1] - sliderMaxSize) > Math.abs(sliderMaxSize - sliderMinSize) * 0.01);

            if (isSizeFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const size = parseFloat(p.parcelsize) || 0;
                    return size >= sizeRange[0] && size <= sizeRange[1];
                });
                console.log(`After Size filter: ${parcelsArray.length} parcels (range: ${sizeRange[0]}-${sizeRange[1]})`);
            }

            // Apply Spread Percentage filter if active
            const isSpreadFilterActive = sliderMinSpread !== undefined && sliderMaxSpread !== undefined &&
                (Math.abs(spreadRange[0] - sliderMinSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01 ||
                    Math.abs(spreadRange[1] - sliderMaxSpread) > Math.abs(sliderMaxSpread - sliderMinSpread) * 0.01);

            if (isSpreadFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const spread = parseFloat(p.spreadpercentage) || 0;
                    return spread >= spreadRange[0] && spread <= spreadRange[1];
                });
                console.log(`After Spread % filter: ${parcelsArray.length} parcels (range: ${spreadRange[0]}-${spreadRange[1]})`);
            }

            // Apply Vacant filter if active
            const isVacantFilterActive = vacantFilter && vacantFilter !== "All";

            if (isVacantFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const isVacant = parseInt(p.is_vaccant) || 0;
                    return isVacant === 1;
                });
                console.log(`After Vacant filter: ${parcelsArray.length} parcels (vacant only)`);
            }

            // Apply Listed Sale filter if active
            const isListedSaleFilterActive = listedSaleFilter && listedSaleFilter !== "All";

            if (isListedSaleFilterActive) {
                parcelsArray = parcelsArray.filter(p => {
                    const listed = (p.listedsale || p.listed_sale || "").toLowerCase();
                    return listed === "all";
                });
                console.log(`After Listed Sale filter: ${parcelsArray.length} parcels (${listedSaleFilter} only)`);
            }

            // Apply Flood Risk filter if active
            const isFloodFilterActive = floodRiskFilter && floodRiskFilter !== "All";

            // if (isFloodFilterActive) {
            if (isFloodFilterActive && !adjacentMode) {
                parcelsArray = parcelsArray.filter(p => {
                    const flood = p.parcelcustomfemaflood || p.FloodFemaRisk || "";
                    return flood.includes(floodRiskFilter);
                });
                console.log(`After Flood Risk filter: ${parcelsArray.length} parcels (${floodRiskFilter} only)`);
            }

            console.log(`Found ${parcelsArray.length} unique parcels in viewport at zoom ${currentZoom.toFixed(2)}`);
            setFilteredParcels(parcelsArray);
        };

        map.on("moveend", updateVisibleParcels);
        map.on("idle", updateVisibleParcels); // Also update after filters change
        map.on("zoomend", updateVisibleParcels); // Update when zoom changes
        console.log("Added moveend, idle, and zoomend listeners for visible parcels update.");

        return () => {
            map.off("moveend", updateVisibleParcels);
            map.off("idle", updateVisibleParcels);
            map.off("zoomend", updateVisibleParcels);
        };
    }, [mapLoaded, zestimateRange, priceRange, sizeRange, vacantFilter, listedSaleFilter, floodRiskFilter, sliderMinZestimate, sliderMaxZestimate, sliderMinPrice, sliderMaxPrice, sliderMinSize, sliderMaxSize]);

    // Manually trigger parcel list update when zoom or county changes
    // This ensures updates happen even if map events don't fire
    useEffect(() => {
        if (!mapRef.current || !mapLoaded) return;
        if (currentZoom < 13.1) return; // Only for zoom >= 13.1
        if (selectedCounty === "all") return;

        const map = mapRef.current;

        // Small delay to ensure map has finished rendering
        const timer = setTimeout(() => {
            // Force update by triggering a map idle event
            map.fire("idle");
        }, 150);

        return () => clearTimeout(timer);
    }, [currentZoom, selectedCounty, mapLoaded, zestimateRangeDragComplete, priceRangeDragComplete, sizeRangeDragComplete]);


    const clearParcelHighlight = () => {
    const map = mapRef.current;
    if (!map) return;

    // if (map.getLayer("parcel-selected-fill")) {
    //     map.setFilter("parcel-selected-fill", ["==", ["get", "parcel"], ""]);
    // }
    if (map.getLayer("parcel-selected-outline")) {
        map.setFilter("parcel-selected-outline", ["==", ["get", "parcel"], ""]);
    }
};

    const updateMapWithSearchParcels = (parcels) => {
    const map = mapRef.current;
    if (!map) return;

    if (!parcels || parcels.length === 0) return;

    // Collect parcel IDs
    // const parcelIds = parcels
    //     .map(p => p.parcel || p.parcelid || p.geo_id || p.id)
    //     .filter(Boolean)
    //     .map(String);

        const firstParcel = parcels[0];

const firstParcelId =
    firstParcel?.parcel ||
    firstParcel?.parcelid ||
    firstParcel?.geo_id ||
    firstParcel?.id;

if (!firstParcelId) return;

const parcelIds = [String(firstParcelId)];

    if (adjacentMode) {
        const touching =
            firstParcel.touching_parcel ||
            firstParcel.adjacent_parcel;

        if (touching) {
            parcelIds.push(String(touching));
        }
    }

    if (parcelIds.length === 0) return;
    
    

    // ⭐ HIGHLIGHT instead of filtering

    // if (map.getLayer("parcel-selected-fill")) {
    //     map.setFilter("parcel-selected-fill", [
    //         "any",
    //         ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
    //         ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
    //     ]);
    //     map.setLayoutProperty("parcel-selected-fill", "visibility", "visible");
    // }

    // if (map.getLayer("parcel-selected-outline")) {
    //     map.setFilter("parcel-selected-outline", [
    //         "any",
    //         ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
    //         ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
    //     ]);
    //     map.setLayoutProperty("parcel-selected-outline", "visibility", "visible");
    // }

//  Outline handling (update OR create)
if (map.getLayer("parcel-selected-outline")) {

    map.setFilter("parcel-selected-outline", [
        "any",
        ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
        ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
    ]);

    map.setLayoutProperty("parcel-selected-outline", "visibility", "visible");

} else if (map.getSource("parcels")) {

    // Create red outline layer if missing
    map.addLayer({
        id: "parcel-selected-outline",
        type: "line",
        source: "parcels",
        "source-layer": "fema",
        layout: { visibility: "visible" },
        paint: {
            "line-color": "#fd0224",
            "line-width": 2.5,
            "line-opacity": 0.95
        },
        filter: [
            "any",
            ["in", ["to-string", ["get", "parcel"]], ["literal", parcelIds]],
            ["in", ["to-string", ["get", "parcelid"]], ["literal", parcelIds]]
        ]
    });

}

    // ⭐ Fly to first parcel
    // const firstParcel = parcels[0];

    const lon = parseFloat(
        firstParcel.longitude ??
        firstParcel.lon ??
        firstParcel.lng ??
        firstParcel.x
    );

    const lat = parseFloat(
        firstParcel.latitude ??
        firstParcel.lat ??
        firstParcel.y
    );

    if (Number.isFinite(lat) && Number.isFinite(lon)) {

        const screenWidth = window.innerWidth;
        const leftPanelWidth = screenWidth * (listWidth / 100);
        const offset = -(leftPanelWidth / 2);

        map.flyTo({
            center: [lon, lat],
            zoom: 16,
            speed: 0.9,
            offset: [offset, 0]
        });
    }
};

    const updateMapWithSearchParcels1 = (parcels) => {
        const map = mapRef.current;
        if (!map || !map.getLayer("parcel-points")) return;

        if (!parcels || parcels.length === 0) {
            // hide parcels if no results
            map.setFilter("parcel-points", ["==", ["get", "parcel"], ""]);
            return;
        }

        // Collect parcel IDs from API results
        const parcelIds = parcels
            .map(p => p.parcel || p.parcelid || p.geo_id || p.id)
            .filter(Boolean);

        if (parcelIds.length === 0) return;

        // Mapbox IN filter
        const filterExpr = [
            "in",
            ["to-string", ["get", "parcel"]],
            ["literal", parcelIds.map(String)]
        ];

        // Apply filter
        map.setLayoutProperty("parcel-points", "visibility", "visible");
        map.setFilter("parcel-points", filterExpr);

        if (map.getLayer("parcel-outline")) {
            map.setFilter("parcel-outline", filterExpr);
        }

        // Fly to first parcel location
        const firstParcel = parcels[0];

        const lon = parseFloat(
            firstParcel.longitude ??
            firstParcel.lon ??
            firstParcel.lng ??
            firstParcel.x
        );

        const lat = parseFloat(
            firstParcel.latitude ??
            firstParcel.lat ??
            firstParcel.y
        );

        if (Number.isFinite(lat) && Number.isFinite(lon)) {

            // Panel offset logic (same as your row click)
            const screenWidth = window.innerWidth;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            map.flyTo({
                center: [lon, lat],
                zoom: 16,
                speed: 0.9,
                offset: [offset, 0]
            });
        }
    };

    // update left panel + show parcels 
    const updateLeftPanelParcels = (countyName) => {

            console.log("=== updateLeftPanelParcels START ===");
    console.log("County:", countyName);
    console.log("Adjacent Mode:", adjacentMode);
    console.log("Selected County:", selectedCounty);
    console.log("Map exists:", !!mapRef.current);

        setIsParcelSearchActive(false);
        const map = mapRef.current;
        // if (!map || !map.getLayer("parcel-points")) return;

          if (!map) {
        console.log("❌ Map not available");
        return;
    }
    
    console.log("Map layers available:", {
        "parcel-points": !!map.getLayer("parcel-points"),
        "parcel-outline": !!map.getLayer("parcel-outline"),
        "parcel-hover": !!map.getLayer("parcel-hover")
    });
    
    if (!map.getLayer("parcel-points")) {
        console.log("❌ parcel-points layer not found");
        return;
    }

        if (!countyName || countyName === "all") {
            console.log("🔄 Clearing all parcels");
            map.setLayoutProperty("parcel-points", "visibility", "none");
            if (map.getLayer("parcel-outline")) {
                map.setLayoutProperty("parcel-outline", "visibility", "none");
            }
            if (map.getLayer("parcel-hover")) {
                map.setLayoutProperty("parcel-hover", "visibility", "none");
            }
            map.setFilter("parcel-points", null);
            if (map.getLayer("parcel-outline")) {
                map.setFilter("parcel-outline", null);
            }
            if (map.getLayer("parcel-hover")) {
                map.setFilter("parcel-hover", null);
            }
            setFilteredParcels([]);
             console.log("=== updateLeftPanelParcels END (CLEARED) ===");
            return;
        }
        console.log("📍 Setting layers visible");
        map.setLayoutProperty("parcel-points", "visibility", "visible");
        if (map.getLayer("parcel-outline")) {
            map.setLayoutProperty("parcel-outline", "visibility", "visible");
        }
        if (map.getLayer("parcel-hover")) {
            map.setLayoutProperty("parcel-hover", "visibility", "visible");
        }

        const filterExpr = ["==", ["downcase", ["get", "county"]], countyName];
        console.log("🔍 Applying filter:", JSON.stringify(filterExpr));
        

        // Apply filter to both layers
        // try {
            map.setFilter("parcel-points", filterExpr);
            if (map.getLayer("parcel-outline")) {
                map.setFilter("parcel-outline", filterExpr);
            }
        // } catch (e) {
        //     map.once("idle", () => {
        //         map.setFilter("parcel-points", filterExpr);
        //         if (map.getLayer("parcel-outline")) {
        //             map.setFilter("parcel-outline", filterExpr);
        //         }
        //     });
        // }

        // Wait until map is idle (tiles loaded) before querying features
          console.log("⏳ Waiting for map idle to query features");
        map.once("idle", () => {
             console.log("🗺️ Map is idle, querying features");
            const features = map.queryRenderedFeatures({ layers: ["parcel-points"] });
             console.log("📊 Raw features found:", features.length);
 
            // Deduplicate features by parcel ID
            const uniqueFeatures = new Map();
            features.forEach(f => {
                const props = f.properties;
                const uniqueId = props.parcel || props.parcelid || props.geo_id || props.id;
                if (uniqueId && !uniqueFeatures.has(uniqueId)) {
                    uniqueFeatures.set(uniqueId, props);
                }
            });

            const uniqueParcels = Array.from(uniqueFeatures.values());
             console.log("📦 Unique parcels:", uniqueParcels.length);
            setFilteredParcels(uniqueParcels);
            console.log(`Found ${uniqueParcels.length} unique parcels in ${countyName} County (${features.length} features total)`);
             console.log("=== updateLeftPanelParcels END ===");
        });
    };


    // --- dropdown change ---
    const onDropdownChange = e => {
        const val = String(e.target.value).toLowerCase();
        setSelectedCounty(val);
        updateLeftPanelParcels(val);
        //fetchTotalCount(val); // Fetch total count for the selected county

        // Reset bounds initialization flag when county changes
        boundsInitialized.current = false;

        // Reset filter touched refs when county changes
        vacantFilterTouched.current = false;
        zoneFilterTouched.current = false;
        listedSaleFilterTouched.current = false;

        // Clear checkbox filters when county changes
        setZoneCodeFilter([]);
        setFloodRiskFilter("All");
        setListedSaleFilter("All");
        setVacantFilter("All");

        // Fetch API data for left panel
        //fetchApiParcels(val);

        // Fly to county center when dropdown changes
        if (mapRef.current && val !== "all") {
            const map = mapRef.current;

            // Query county features to get geometry
            const features = map.querySourceFeatures('nc-counties', {
                sourceLayer: 'counties',
                filter: ['==', ['downcase', ['get', 'County']], val]
            });

            if (features.length === 0) {
                console.warn('No features found for county:', val);
                // Fallback to center-based navigation
                const center = countyCenters[val] || NC_CENTER;
                const screenWidth = window.innerWidth;
                const leftPanelWidth = screenWidth * (listWidth / 100);
                const offset = -(leftPanelWidth / 2);
                map.flyTo({ center, zoom: 9, speed: 0.8, offset: [offset, 0] });
                return;
            }

            // Use first feature to compute bounds (same as county click)
            const feature = features[0];
            let bounds = new mapboxgl.LngLatBounds();

            const geom = feature.geometry;

            if (geom.type === "Polygon") {
                geom.coordinates[0].forEach(coord => bounds.extend(coord));
            } else if (geom.type === "MultiPolygon") {
                geom.coordinates.forEach(polygon => {
                    polygon[0].forEach(coord => bounds.extend(coord));
                });
            }

            const center = bounds.getCenter();

            if (!Number.isFinite(center.lng) || !Number.isFinite(center.lat)) {
                console.warn("Invalid county center:", feature);
                return;
            }

            const screenWidth = window.innerWidth;
            const leftPanelWidth = screenWidth * (listWidth / 100);
            const offset = -(leftPanelWidth / 2);

            map.flyTo({
                center: [center.lng, center.lat],
                zoom: 9,
                speed: 0.8,
                offset: [offset, 0],
            });
            // const originalCenter = countyCenters[val] || NC_CENTER;

            // // Get current map bounds
            // const bounds = map.getBounds();

            // // Calculate map width in km (west → east at same latitude)
            // const mapWidthKm = turf.distance(
            //     [bounds.getWest(), originalCenter[1]],
            //     [bounds.getEast(), originalCenter[1]],
            //     { units: "kilometers" }
            // );

            // // Left panel covers 60%, so shift center to middle of visible 40%
            // // → shift east by 30% of total width
            // const shiftKm = mapWidthKm * 0.4;

            // // Create shifted center using Turf
            // const shiftedCenter = turf.destination(
            //     originalCenter,
            //     shiftKm,
            //     90, // 90° = east
            //     { units: "kilometers" }
            // );

            // map.flyTo({
            //     center: shiftedCenter.geometry.coordinates,
            //     zoom: 9,
            //     speed: 0.8,
            // });
        } else if (mapRef.current && val === "all") {
            // Fly back to NC center when "all" is selected
            mapRef.current.flyTo({
                center: NC_CENTER,
                zoom: 6.5,
                speed: 0.8,
            });
        }
    };


    // --- PropertyList row click handler ---
    const handleRowClick = (property) => {
        if (!mapRef.current) return;

        const parcelId =
            property.parcel ||
            property.parcelid ||
            property.geo_id ||
            property.id;

        // setSelectedParcelId(parcelId);

        // Get adjacent/touching parcel from property (already available from API)
        const touchingId = property?.touching_parcel || property?.adjacent_parcel || null;

        // Use composite key in adjacent mode so rows with same parcel but different adjacent partner are unique
        const compositeKey = adjacentMode ? `${parcelId}-${touchingId || ''}` : parcelId;
        setSelectedParcelId(compositeKey);
        console.log('Row click compositeKey:', compositeKey, 'adjacentMode:', adjacentMode);


        const lon = parseFloat(property.longitude ?? property.lon ?? property.lng ?? property.x);
        const lat = parseFloat(property.latitude ?? property.lat ?? property.y);

        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

        // Show loading indicator
        // setIsLoadingParcel(true);

        // Calculate offset for left panel
        const screenWidth = window.innerWidth;
        // const leftPanelWidth = screenWidth * 0.60;
        const leftPanelWidth = screenWidth * (listWidth / 100);
        
        const offset = -(leftPanelWidth / 2);

        // Fly to parcel
        mapRef.current.flyTo({
            center: [lon, lat],
            // zoom: 18,
            zoom: 15,
            speed: 1.2,
            offset: [offset, 0]
        });

        // mapRef.current.once("moveend", () => {
        //     setIsLoadingParcel(false);
        // });

        //  DO NOT TOUCH hover popup
        // Only remove previous CLICK popup
        clickPopupRef.current?.remove();
        clickPopupRef.current = null;

        // Compute Adjacent Parcel ID (parcel + 1) purely for display

        // let adjacentParcelIdDisplay = null;
        // try {
        //     const pidStr = parcelId != null ? String(parcelId) : null;
        //     const match = pidStr ? pidStr.match(/\d+/) : null;
        //     if (match) {
        //         const orig = match[0];
        //         const next = String(Number(orig) + 1);
        //         const padded = next.padStart(orig.length, '0');
        //         adjacentParcelIdDisplay = pidStr.replace(orig, padded);
        //     }
        // } catch (e) { }

        // Only include adjacent parcel info in popup when in adjacentMode
        const adjacentParcelInfoHTML = adjacentMode
            && touchingId
                ? `<div><strong>Adjacent Parcel ID:</strong> ${touchingId}</div>`
                // : `<div><strong>Adjacent Parcel ID:</strong> ${adjacentParcelIdDisplay || 'N/A'}</div>`
            : '';

        // console.log(' Popup debug - adjacentMode:', adjacentMode, 'touchingId:', touchingId, 'adjacentParcelIdDisplay:', adjacentParcelIdDisplay);
        console.log(' Popup debug - adjacentParcelInfoHTML:', adjacentParcelInfoHTML);
        console.log(' Popup debug - coordinates:', [lon, lat]);

        // Ensure map is ready before creating popup
        const createPopup = () => {
            console.log(' Creating popup immediately...');
            clickPopupRef.current = new mapboxgl.Popup({
                closeOnClick: false,
                closeButton: true,
                maxWidth: "300px",
                className: adjacentMode 
                 ? "parcel-popup fixed-popup" 
                 : "parcel-popup",
                // offset: [0, -10], // Offset to avoid overlap with map controls
                // anchor: adjacentMode ? "right" : "top-right"
                 anchor: 'top-left'
            })
                .setLngLat([lon, lat])
                .setHTML(`
                    <div style="
                        background:#e1faee;
                        padding:14px 14px;
                        border-radius:10px;
                        
                        box-shadow:0 6px 18px rgba(0,0,0,0.18);
                        font-family:Inter, sans-serif;
                        font-size:13px;
                        width:270px;
                    ">
            
                        <!-- Header -->
                        <div style="
                            font-size:14px;
                            font-weight:600;
                            color:#1c8f7d;
                            margin-bottom:10px;
                            border-bottom:1px solid #e6e6e6;
                            padding-bottom:6px;
                        ">
                            <span style="text-transform: capitalize;">${property.county || "N/A"}</span> |
                             <span style="text-transform: capitalize;">${property.municipality || "N/A"}</span> |
                            <span>${property.parcel || property.parcelid || "N/A"}</span>
                           
                        </div> 
            
                        <!-- Content -->
                        <div style="line-height:1.7; color:#333;">
                          
                           
                         ${!adjacentMode ? `
                          <div><strong>Address:</strong> ${property.addrline || property.addrline1 || "N/A"}</div>
                         <div><strong>Flood Zone:</strong> ${property.parcelcustomfemaflood || "N/A"}</div>
                         ` : ''}
                            <div><strong>Zoning:</strong> ${property.zonedcodelocal || "N/A"}</div>
                            ${adjacentParcelInfoHTML}
                            <div>
                                <strong>Parcel Size:</strong>
                                ${property.parcelsize
                        ? `${parseFloat(property.parcelsize).toFixed(2)} acres`
                        : "N/A"}
                            </div>
                            <div><strong>Max Units:</strong> ${property.totalunits || "N/A"}</div>
                            <div><strong>Price:</strong> ${property.zestimate ? `$${parseFloat(property.zestimate).toLocaleString('en-US')}` : "N/A"}</div>
                            <div><strong>20%Developed value:</strong> ${property.totallandvalue ? `$${parseFloat(property.totallandvalue).toLocaleString('en-US')}` : "N/A"}</div>

                            <div>
  <strong>Spread:</strong> ${property.spread ? property.spread < 0
                        ? `-$${Math.abs(property.spread).toLocaleString("en-US")}`
                        : `$${property.spread.toLocaleString("en-US")}`
                        : "N/A"}
  
     ${property.spreadpercentage
                        ? `(${property.spreadpercentage}%)`
                        : "N/A"}
</div>
                            
                        </div>
            
                    </div>
                `)
                .addTo(mapRef.current);

            console.log(' Popup created and added to map');
        };

        // Check if map is ready, if not wait for it
        if (mapRef.current.isStyleLoaded()) {
            createPopup();
        } else {
            console.log(' Map style not loaded, waiting...');
            mapRef.current.once('styledata', () => {
                console.log(' Map style loaded, creating popup');
                createPopup();
            });
        }

        // Highlight the selected parcel and its adjacent parcel
        try {
            const ids = [];
            if (parcelId) {
                const sId = String(parcelId);
                ids.push(sId);
                // Prefer adjacent id from API when available

                // ONLY add adjacent parcel if adjacent mode is ON
                if (adjacentMode) {
                    const touching = property?.touching_parcel || property?.adjacent_parcel;
                    if (touching) {
                        ids.push(String(touching));
                    } 
                    // else {
                    //     // Fallback: compute +1 id if numeric segment exists
                    //     const numeric = sId.match(/\d+/);
                    //     if (numeric) {
                    //         const origNum = numeric[0];
                    //         const next = String(Number(origNum) + 1);
                    //         const nextPadded = next.padStart(origNum.length, '0');
                    //         // Padded and unpadded variants
                    //         const nextIdPadded = sId.replace(origNum, nextPadded);
                    //         const nextIdUnpadded = sId.replace(origNum, next);
                    //         ids.push(nextIdPadded);
                    //         if (nextIdUnpadded !== nextIdPadded) ids.push(nextIdUnpadded);
                    //     }
                    // }
                }
            }

            // if (mapRef.current.getLayer('parcel-selected-fill')) {
            //     mapRef.current.setFilter('parcel-selected-fill', [
            //         'any',
            //         ['in', ['get', 'parcel'], ['literal', ids]],
            //         ['in', ['get', 'parcelid'], ['literal', ids]]
            //     ]);
            //     mapRef.current.setLayoutProperty('parcel-selected-fill', 'visibility', 'visible');
            // }
            if (mapRef.current.getLayer('parcel-selected-outline')) {
                mapRef.current.setFilter('parcel-selected-outline', [
                    'any',
                    ['in', ['get', 'parcel'], ['literal', ids]],
                    ['in', ['get', 'parcelid'], ['literal', ids]]
                ]);
                mapRef.current.setLayoutProperty('parcel-selected-outline', 'visibility', 'visible');
            } else {
                // Create parcel-selected-outline layer if it doesn't exist
                mapRef.current.addLayer({
                    id: "parcel-selected-outline",
                    type: "line",
                    source: "parcels",
                    "source-layer": "fema",
                    layout: { visibility: "visible" },
                    paint: {
                        "line-color": "#fd0224",
                        "line-width": 2.5,
                        "line-opacity": 0.95
                    },
                    filter: [
                        'any',
                        ['in', ['get', 'parcel'], ['literal', ids]],
                        ['in', ['get', 'parcelid'], ['literal', ids]]
                    ]
                });
            }
        } catch (e) { }
    };

    // Check if any filters (other than county) are active - determines data source display
    // NOTE: Sorting is NOT included here because it doesn't filter data, just orders it
    // Tile data can still be shown with sorting applied

    const hasAnyFilterActive =
        priceRangeDragComplete !== 0 ||
        zestimateRangeDragComplete !== 0 ||
        sizeRangeDragComplete !== 0 ||
        spreadRangeDragComplete !== 0 ||
        (zoneCodeFilter && zoneCodeFilter.length > 0) ||
        (vacantFilter && vacantFilter !== "All") ||
        (listedSaleFilter && listedSaleFilter !== "All") ||
        (floodRiskFilter && floodRiskFilter !== "All");


    return (
        <>
            {/* Header */}
            <Header
                activeTab={"parcels"}
                showSearch={false}
                showFavorites={false}
                showLinks={true}
                isSubscribed={isSubscribed}
                isMenuOpen={isMenuOpen}
                toggleMenu={toggleMenu}
                handleLogout={handleLogout}
                // County selector props
                showCountySelector={true}
                selectedCounty={selectedCounty}
                // countiesList={countiesList}
                countiesList={['Chatham','Wake']}
                onCountyChange={onDropdownChange}

                onParcelSearch={searchParcelApi}
                clearParcelSearch={clearParcelSearch}
                setIsParcelSearchActive={setIsParcelSearchActive}
                resetParcelSearchSignal={resetParcelSearchSignal}
                isAdjacentMode={adjacentMode}
                onAdjacentToggle={(isAdjacent) => {
                    setAdjacentMode(isAdjacent);
                    reloadParcelTiles(isAdjacent);
                    // Tell Header to reset parcel search UI
                    setResetParcelSearchSignal(prev => prev + 1);

                    // Clear list immediately so UI doesn't show stale results
                    //    setApiParcels([]);
                    //    setFilteredApiParcels([]);
                    // Reapply county filter to map AFTER tiles reload

                    const applyCountyToMap = () => {
                        const county = selectedCountyRef.current;
                        if (!mapRef.current || !county || county === "all") return;
                        try {
                            // ensure visible  set county filter
                            mapRef.current.setLayoutProperty("parcel-points", "visibility", "visible");
                            const filterExpr = ["==", ["downcase", ["get", "county"]], county];
                            mapRef.current.setFilter("parcel-points", filterExpr);
                            if (mapRef.current.getLayer("parcel-outline")) {
                                mapRef.current.setLayoutProperty("parcel-outline", "visibility", "visible");
                                mapRef.current.setFilter("parcel-outline", filterExpr);
                            }
                            // re-query rendered features and populate left panel reliably
                            setTimeout(() => {
                                try {
                                    const feats = mapRef.current.queryRenderedFeatures({ layers: ["parcel-points"] }) || [];
                                    const uniques = new Map();
                                    feats.forEach(f => {
                                        const p = f.properties || {};
                                        const id = p.parcel || p.parcelid || p.geo_id || p.id;
                                        if (id && !uniques.has(id)) uniques.set(id, p);
                                    });
                                    setFilteredParcels(Array.from(uniques.values()));
                                    console.log(`Applied county filter after tile swap; parcels: ${uniques.size}`);
                                } catch (e) { console.warn('queryRenderedFeatures failed', e); }
                            }, 150);
                        } catch (e) { console.warn('applyCountyToMap failed', e); }
                    };

                    if (mapRef.current) {
                        mapRef.current.once("sourcedata", applyCountyToMap);
                        mapRef.current.once("idle", applyCountyToMap);
                        // fallback
                        setTimeout(applyCountyToMap, 800);
                    } else {
                        applyCountyToMap();
                    }


                    if (selectedCounty && selectedCounty !== "all") {
                        fetchApiParcels(
                            selectedCounty,
                            priceRange[0],
                            priceRange[1],
                            zestimateRange[0],
                            zestimateRange[1],
                            sizeRange[0],
                            sizeRange[1],
                            spreadRange[0],
                            spreadRange[1],
                            zoneCodeFilter && zoneCodeFilter.length > 0 ? zoneCodeFilter : null,
                            vacantFilter !== "All" ? vacantFilter : null,
                            listedSaleFilter !== "All" ? listedSaleFilter : null,
                            floodRiskFilter !== "All" ? floodRiskFilter : null,
                            sortBy,
                            sortOrder,
                            municipalityFilter !== "All" ? municipalityFilter : null,
                            0
                        );
                    }
                }}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "100px",
                    zIndex: 2000,
                }}
            />

            {/* Parcel Filters - Always visible */}
            {selectedCounty !== "all" &&
            <div style={{
                position: "fixed",
                top: "100px",
                left: 0,
                right: 0,
                // background: "#fff",
                background: "#deefea !important",
                zIndex: 1850,
                // zIndex: 0,
            }}>
                <ParcelFilters
                    // County selection
                    selectedCounty={selectedCounty}
                    countiesList={countiesList}
                    onCountyChange={onDropdownChange}

                    // Mode
                    isAdjacentMode={adjacentMode}

                    // Display info
                    currentZoom={currentZoom}
                    apiParcelsCount={apiParcels.length}
                    tileParcelsCount={filteredParcels.length}

                    // Filter visibility
                    showPriceFilter={showPriceFilter}
                    setShowPriceFilter={setShowPriceFilter}
                    showSizeFilter={showSizeFilter}
                    setShowSizeFilter={setShowSizeFilter}
                    showFloodFilter={showFloodFilter}
                    setShowFloodFilter={setShowFloodFilter}
                    showZoneFilter={showZoneFilter}
                    setShowZoneFilter={setShowZoneFilter}
                    showZestimateFilter={showZestimateFilter}
                    setShowZestimateFilter={setShowZestimateFilter}
                    showSpreadFilter={showSpreadFilter}
                    setShowSpreadFilter={setShowSpreadFilter}

                    showMunicipalityFilter={showMunicipalityFilter}
                    setShowMunicipalityFilter={setShowMunicipalityFilter}

                    showAdjacentParcelFilter={showAdjacentParcelFilter}
                    setShowAdjacentParcelFilter={setShowAdjacentParcelFilter}
                    adjacentParcelFilter={adjacentParcelFilter}
                    setAdjacentParcelFilter={setAdjacentParcelFilter}

                    // Filter values
                    priceRange={priceRange}
                    setPriceRange={setPriceRange}
                    sizeRange={sizeRange}
                    setSizeRange={setSizeRange}
                    floodRiskFilter={floodRiskFilter}
                    setFloodRiskFilter={setFloodRiskFilter}
                    zoneCodeFilter={zoneCodeFilter}
                    setZoneCodeFilter={setZoneCodeFilter}
                    zestimateRange={zestimateRange}
                    setZestimateRange={setZestimateRange}
                    spreadRange={spreadRange}
                    setSpreadRange={setSpreadRange}
                    listedSaleFilter={listedSaleFilter}
                    setListedSaleFilter={setListedSaleFilter}
                    vacantFilter={vacantFilter}
                    setVacantFilter={setVacantFilter}
                    municipalityFilter={municipalityFilter}
                    setMunicipalityFilter={setMunicipalityFilter}
                    municipalityOptions={municipalityOptions}

                    // Slider bounds
                    sliderMinPrice={sliderMinPrice}
                    sliderMaxPrice={sliderMaxPrice}
                    sliderMinSize={sliderMinSize}
                    sliderMaxSize={sliderMaxSize}
                    sliderMinZestimate={sliderMinZestimate}
                    sliderMaxZestimate={sliderMaxZestimate}
                    sliderMinSpread={sliderMinSpread}
                    sliderMaxSpread={sliderMaxSpread}


                    // Filter options
                    floodOptions={floodOptions}
                    zoneCodes={zoneCodes}
                    listedSaleOptions={listedSaleOptions}

                    // Helper functions
                    clearAllFilters={clearAllFilters}
                    formatCurrency={formatCurrency}

                    // Callback for slider drag complete
                    onPriceRangeChangeComplete={handlePriceRangeChangeComplete}
                    onZestimateRangeChangeComplete={handleZestimateRangeChangeComplete}
                    onSizeRangeChangeComplete={handleSizeRangeChangeComplete}
                    onSpreadRangeChangeComplete={handleSpreadRangeChangeComplete}
                />
            </div>
}

            {/* Left panel - API data (zoom < 13.1) or Tile data (zoom >= 13.1) */}
            {selectedCounty !== "all" && (() => {
                // Check if any filters are active - use actual values, not just touched flags
                const hasAnyFilterActive =
                    (vacantFilter && vacantFilter !== "All") ||
                    (zoneCodeFilter && zoneCodeFilter.length > 0) ||
                    (listedSaleFilter && listedSaleFilter !== "All") ||
                    (floodRiskFilter && floodRiskFilter !== "All") ||
                    // municipalityFilterTouched.current ||
                    (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice) ||
                    (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate) ||
                    (sizeRange[0] !== sliderMinSize || sizeRange[1] !== sliderMaxSize) ||
                    (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

                return (
                    <>
                        <div
                            style={{
                                position: "absolute",
                                top: "160px",
                                left: 0,
                                // width: "60%",
                                width: `${listWidth}%`,
                                bottom: 0,
                                background: "#fafafa",
                                borderRight: "1px solid #ddd",
                                overflow: "auto",
                                zIndex: 1500,
                            }}
                        >
                            {isLoadingParcel ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                    Loading parcels...
                                </div>
                                // ) : isLoadingApi && currentZoom < 13.1 ? (
                            ) : isLoadingApi ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                    Loading parcels...
                                </div>
                            ) : currentZoom >= 13.1 && !hasAnyFilterActive &&   !isParcelSearchActive && !adjacentMode ? (
                                // Show tile server data ONLY when zoomed in AND no filters are active

                                filteredParcels.length === 0 ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        No parcels visible at this zoom level
                                    </div>
                                ) : (
                                    <>
                                        {/* Property Count Display */}
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                            display: "none"
                                        }}>
                                            {(() => {
                                                const useTiles = currentZoom >= 13.1 && !hasAnyFilterActive && !adjacentMode;
                                                const displayCount = useTiles ? filteredParcels.length : totalParcelCount;
                                                const source = useTiles ? "(Map Tiles)" : "(Total)";
                                                return `${displayCount.toLocaleString()} Properties ${source}`;
                                            })()}
                                        </div>
                                        {console.log(`Passing ${filteredParcels.length} parcels to PropertyList`)}
                                        <PropertyList
                                            parcels={filteredParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={null}
                                            setSortKey={null}
                                            sortDir={null}
                                            setSortDir={null}
                                            currentZoom={currentZoom}
                                            isAdjacentMode={adjacentMode}
                                        />
                                    </>
                                )
                                // ) : filteredApiParcels.length === 0 && !isLoadingApi ? (
                                //     // Show "no parcels" only if not loading AND data is empty
                                //     <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                //         No parcels found
                                //     </div>
                            ) : (
                                // Show API data when zoomed out (use filtered data)--before

                                // Show API data when zoomed out OR when any filter is active (regardless of zoom)
                                filteredApiParcels.length === 0 ? (
                                    <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
                                        {apiParcels.length === 0 ? "No parcels found" : "No parcels match the current filters"}
                                    </div>
                                ) : (
                                    <>
                                        {/* Property Count Display - Filtered API Data */}
                                        <div style={{
                                            padding: "10px 15px",
                                            background: "#f5f5f5",
                                            borderBottom: "1px solid #e0e0e0",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333",
                                            display: "none"
                                        }}>
                                            {totalParcelCount.toLocaleString()} Properties (Total)
                                        </div>
                                        <PropertyList
                                            parcels={filteredApiParcels}
                                            onRowClick={handleRowClick}
                                            selectedParcelId={selectedParcelId}
                                            setSelectedParcelId={setSelectedParcelId}
                                            sortKey={sortBy}
                                            setSortKey={setSortBy}
                                            sortDir={sortOrder}
                                            setSortDir={setSortOrder}
                                            currentZoom={currentZoom}
                                            loadMore={loadMoreParcels}
                                            hasMore={hasMoreData}
                                            isLoadingMore={isLoadingMore}
                                            isAdjacentMode={adjacentMode}
                                        />
                                    </>
                                )
                            )}
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                top: "160px",
                                left: `${listWidth}%`,
                                width: "6px",
                                bottom: 0,
                                cursor: "col-resize",
                                background: "#ddd",
                                zIndex: 1600,
                            }}
                            onMouseDown={(e) => {
                                const startX = e.clientX;

                                const onMouseMove = (moveEvent) => {
                                    const delta = moveEvent.clientX - startX;
                                    const newWidth =
                                        ((listWidth / 100) * window.innerWidth + delta) /
                                        window.innerWidth *
                                        100;

                                    if (newWidth > 20 && newWidth < 80) {
                                        setListWidth(newWidth);
                                    }
                                };

                                const onMouseUp = () => {
                                    window.removeEventListener("mousemove", onMouseMove);
                                    window.removeEventListener("mouseup", onMouseUp);
                                };

                                window.addEventListener("mousemove", onMouseMove);
                                window.addEventListener("mouseup", onMouseUp);
                            }}
                        />
                    </>
                );
            })()}

            {mapInitError && (
                <div style={{ padding: "16px", color: "red" }}>
                    Map failed to load. Please refresh.
                </div>
            )}

            {/* <ErrorBoundary> */}
            {/* Map - Tile server data */}
            {/* <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}> */}
            <div
                ref={mapContainerRef}
                style={{
                    position: "absolute",
                    top: selectedCounty !== "all" ? "160px" : "100px",
                    // left: selectedCounty !== "all" ? "60%" : 0,
                    left: selectedCounty !== "all" ? `${listWidth}%` : 0,
                    right: 0,
                    bottom: 0,
                    minHeight: 300,
                }}
            />
            {/* </div> */}
            {/* </ErrorBoundary> */}
        </>
    );
};

export default NCComponent;
